import React, { useEffect, useMemo, useState } from "react";
import I from "./Icon";
import BC from "./Breadcrumb";
import apiService from "../services/apiService";
import goreLogo from "./Images/gore logo (1).jpg";

const STEPS = [
  {
    id: "personal",
    label: "Personal",
    short: "Personal",
  },
  {
    id: "academic",
    label: "Academic & Exams",
    short: "Academic",
  },
  {
    id: "courier",
    label: "Books & Courier",
    short: "Books",
  },
  {
    id: "review",
    label: "Review",
    short: "Review",
  },
  {
    id: "payment",
    label: "Payment",
    short: "Payment",
  },
];
/* =========================================================
   OPTIONS
========================================================= */
const showNotification = (type, message) => {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined"
  ) {
    return;
  }
  const existingNotification =
    document.getElementById(
      "student-form-notification"
    );
  if (existingNotification) {
    existingNotification.remove();
  }
  const notification =
    document.createElement("div");
  notification.id =
    "student-form-notification";
  notification.textContent =
    `${type === "success" ? "✓" : "✕"} ${message}`;
  Object.assign(
    notification.style,
    {
      position: "fixed",
      top: "20px",
      right: "20px",
      zIndex: "99999",
      minWidth: "280px",
      maxWidth: "420px",
      padding: "12px 16px",
      borderRadius: "8px",
      backgroundColor:
        type === "success"
          ? "#d1e7dd"
          : "#f8d7da",
      color:
        type === "success"
          ? "#0f5132"
          : "#842029",
      border:
        type === "success"
          ? "1px solid #badbcc"
          : "1px solid #f5c2c7",
      fontSize: "14px",
      fontWeight: "600",
      boxShadow:
        "0 4px 12px rgba(0,0,0,0.15)",
    }
  );
  document.body.appendChild(
    notification
  );
  window.setTimeout(() => {
    if (notification.parentNode) {
      notification.remove();
    }
  }, 3000);
};
const getApiErrorMessage = (
  error,
  fallback
) => {
  const data =
    error?.response?.data;
  if (
    typeof data === "string" &&
    data.trim()
  ) {
    return data;
  }
  if (
    data &&
    typeof data === "object"
  ) {
    if (
      data?.errors &&
      typeof data.errors === "object"
    ) {
      const validationErrors =
        Object.entries(data.errors)
          .flatMap(
            ([key, value]) => {
              const messages =
                Array.isArray(value)
                  ? value
                  : [value];
              return messages.map(
                (message) =>
                  `${key}: ${message}`
              );
            }
          )
          .filter(Boolean)
          .join(" | ");
      if (validationErrors) {
        return validationErrors;
      }
    }
    return (
      data?.failureReason ||
      data?.FailureReason ||
      data?.message ||
      data?.Message ||
      data?.title ||
      data?.Title ||
      data?.error ||
      error?.message ||
      fallback
    );
  }
  return (
    error?.message ||
    fallback
  );
};
const DELIVERY_OPTIONS = [
  {
    value: "office",
    label: "Collect from Office / School",
    mr: "ऑफिस / शाळेतून घेणार",
  },
  {
    value: "courier",
    label: "Courier Delivery",
    mr: "कुरिअरद्वारे पाठवा",
  },
];
/* =========================================================
   API LOOKUP HELPERS
========================================================= */
const pickValue = (obj, keys, fallback = null) => {
  if (obj === null || obj === undefined) return fallback;
  if (typeof obj !== "object") {
    return obj;
  }
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return fallback;
};
const extractApiList = (payload, depth = 0) => {
  if (depth > 8) return [];
  if (Array.isArray(payload)) return payload;
  if (!payload) return [];
  // Some APIs return JSON as a string.
  if (typeof payload === "string") {
    try {
      return extractApiList(JSON.parse(payload), depth + 1);
    } catch {
      return [];
    }
  }
  if (typeof payload !== "object") return [];
  // Prefer the most common API wrapper property names first.
  const possibleKeys = [
    "output", "Output",
    "data", "Data",
    "result", "Result",
    "response", "Response",
    "value", "Value",
    "items", "Items",
    "rows", "Rows",
    "schools", "Schools",
    "records", "Records",
    "list", "List",
    "model", "Model",
    "models", "Models",
  ];
  for (const key of possibleKeys) {
    const value = payload?.[key];
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      const nested = extractApiList(value, depth + 1);
      if (nested.length > 0) return nested;
    }
  }
  // Final fallback: recursively inspect every property.
  // This makes lookup dropdowns work even when the backend uses
  // a custom wrapper name that the frontend does not know yet.
  for (const value of Object.values(payload)) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      const nested = extractApiList(value, depth + 1);
      if (nested.length > 0) return nested;
    }
  }
  return [];
};
const findFallbackId = (item) => {
  if (!item || typeof item !== "object") return item ?? null;
  const entry = Object.entries(item).find(([key, value]) =>
    /(^id$|id$)/i.test(key) &&
    value !== null &&
    value !== undefined &&
    value !== "" &&
    (typeof value === "number" || typeof value === "string")
  );
  return entry?.[1] ?? null;
};
const findFallbackName = (item, id) => {
  if (!item || typeof item !== "object") return id ?? "";
  // First prefer semantic display fields.
  const preferred = Object.entries(item).find(([key, value]) =>
    /(name|title|standard|class|medium|status|school|description|desc|label)/i.test(key) &&
    !/id$/i.test(key) &&
    typeof value === "string" &&
    value.trim() !== ""
  );
  if (preferred) return preferred[1];
  // Then use the first useful string that is not an audit/system field.
  const fallback = Object.entries(item).find(([key, value]) =>
    typeof value === "string" &&
    value.trim() !== "" &&
    !/(created|modified|updated|deleted|token|password|document|code)/i.test(key)
  );
  return fallback?.[1] ?? id ?? "";
};
const normalizeOption = (item, idKeys, nameKeys) => {
  const id = pickValue(item, idKeys, findFallbackId(item));
  const name = pickValue(item, nameKeys, findFallbackName(item, id));
  return {
    id,
    name: name === null || name === undefined ? "" : String(name),
    raw: item,
  };
};
const normalizeStandard = (item) =>
  normalizeOption(
    item,
    [
      "id", "Id",
      "stdId", "StdId",
      "standardId", "StandardId",
      "standardMasterId", "StandardMasterId",
      "classId", "ClassId",
    ],
    [
      "name", "Name",
      "stdName", "StdName",
      "standard", "Standard",
      "standardName", "StandardName",
      "standardMasterName", "StandardMasterName",
      "className", "ClassName",
      "class", "Class",
    ]
  );
const normalizeMedium = (item) => normalizeOption(
  item,
  ["examTypeId", "ExamTypeId", "id", "Id"],
  ["typeName", "TypeName", "examTypeName", "ExamTypeName", "name", "Name", "description", "Description"]
);
const isActiveItem = (item) => {
  const active =
    item?.isActive ??
    item?.IsActive;
  return (
    active === true ||
    active === 1 ||
    active === "1" ||
    String(active).toLowerCase() === "true"
  );
};
const normalizeSchool = (item) =>
  normalizeOption(
    item,
    [
      "id", "Id",
      "schoolId", "SchoolId",
      "schoolMasterId", "SchoolMasterId",
    ],
    [
      "name", "Name",
      "schoolName", "SchoolName",
      "school", "School",
      "schoolTitle", "SchoolTitle",
    ]
  );
// Values remain backend IDs; labels come from API name fields or optional master data.
const locationKeys = {
  state: { ids: ["stateId", "StateId"], names: ["stateName", "StateName", "state", "State"] },
  district: { ids: ["districtId", "DistrictId"], names: ["districtName", "DistrictName", "district", "District"] },
  taluka: { ids: ["talukaId", "TalukaId", "talukId", "TalukId", "tehsilId", "TehsilId"], names: ["talukaName", "TalukaName", "talukName", "TalukName", "tehsilName", "TehsilName", "taluka", "Taluka"] }
};
const locationParts = (school, kind) => {
  const raw = school?.raw || school || {};
  const fields = locationKeys[kind];
  const nested = pickValue(raw, [kind + "Master", kind[0].toUpperCase() + kind.slice(1) + "Master", kind + "Details", kind[0].toUpperCase() + kind.slice(1) + "Details"], {});
  const id = pickValue(raw, fields.ids, pickValue(nested, ["id", "Id", ...fields.ids], ""));
  const rawName = pickValue(raw, fields.names, pickValue(nested, ["name", "Name", ...fields.names], ""));
  const name = typeof rawName === "string" ? rawName.trim() : "";
  return { id: id === null ? "" : String(id), name };
};
const locationValue = (school, kind) => {
  const { id, name } = locationParts(school, kind);
  return id || name;
};
const normalizeLocationList = (items, kind) => (items || []).map(item => {
  const { id, name } = locationParts(item, kind);
  const value = id || name;
  return { value, label: name || "", id };
}).filter(item => item.value);
const locationDisplayOptions = (items, kind, masters = []) => {
  const masterRows = normalizeLocationList(masters, kind);
  const masterMap = new Map(masterRows.map(x => [String(x.value), x.label]));
  const map = new Map();
  items.forEach(item => {
    const row = locationParts(item, kind);
    const value = row.id || row.name;
    if (!value) return;
    const name = row.name || masterMap.get(String(value)) || "";
    const previous = map.get(String(value));
    if (!previous || (!previous.label && name)) map.set(String(value), { value: String(value), label: name });
  });
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
};
const stateIsMaharashtra = (school) => {
  const part = locationParts(school, "state");
  return part.name.toLowerCase() === "maharashtra" || (!part.id && part.name.toLowerCase() === "maharashtra");
};
const normalizeExam = (item) => {
  if (!item || typeof item !== "object") {
    return { id: item, name: String(item ?? ""), fee: 0, raw: item };
  }
  const id = pickValue(
    item,
    ["id", "Id", "examId", "ExamId", "examMasterId", "ExamMasterId", "value", "Value"],
    findFallbackId(item)
  );
  return {
    ...item,
    id,
    name: String(
      pickValue(
        item,
        [
          "name", "Name", "examName", "ExamName",
          "examTitle", "ExamTitle", "title", "Title", "label", "Label",
        ],
        findFallbackName(item, id)
      )
    ),
    fee: Number(
      pickValue(
        item,
        [
          "fee", "Fee", "fees", "Fees", "amount", "Amount",
          "price", "Price", "examFee", "ExamFee",
          "examTotalFees", "ExamTotalFees",
          "registrationFee", "RegistrationFee",
        ],
        0
      )
    ) || 0,
    raw: item,
  };
};
/* Exam master fields may be PascalCase or camelCase. */
const examField = (exam, keys, fallback = "") => pickValue(exam, keys, fallback);
const examDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const examIsClosed = (exam) => {
  const state = examField(exam, ["registrationClosed", "RegistrationClosed", "isRegistrationClosed", "IsRegistrationClosed"], null);
  if (state === true || state === 1 || state === "1" || String(state).toLowerCase() === "true") return true;
  const status = examField(exam, ["registrationStatus", "RegistrationStatus", "examStatus", "ExamStatus"], "");
  if (/closed|registration ended/i.test(String(status))) return true;
  const end = examField(exam, ["registrationEndDate", "RegistrationEndDate", "lastDate", "LastDate", "lastDateOfRegistration", "LastDateOfRegistration", "endDate", "EndDate"], null);
  if (!end) return false;
  const parsed = new Date(end);
  return !Number.isNaN(parsed.getTime()) && parsed.getTime() < new Date(new Date().setHours(0, 0, 0, 0));
};
// Use each exam's image from the API. Add a field here if your backend uses another name.
const getExamImage = (exam) => {
  const raw = examField(exam, [
    "imageUrl", "ImageUrl", "imageURL", "ImageURL",
    "examImageUrl", "ExamImageUrl", "examImage", "ExamImage",
    "image", "Image", "logoUrl", "LogoUrl", "logo", "Logo",
    "photoUrl", "PhotoUrl", "imagePath", "ImagePath"
  ], "");
  if (typeof raw !== "string" || !raw.trim()) return "";
  const value = raw.trim();
  // Relative URLs starting with / are served from the React application's public directory.
  if (/^(https?:\/\/|data:image\/|blob:|\/)/i.test(value)) return value;
  return `/${value.replace(/^\.\//, "")}`;
};
// Generic exam illustration for an exam without an image (never initials).
const ExamFallbackArt = () => (
  <svg width="46" height="46" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="58" height="58" rx="15" fill="#EAF1FA" />
    <path d="M11 25L32 15L53 25L32 35L11 25Z" fill="#234C80" />
    <path d="M21 32V41C27 47 37 47 43 41V32" stroke="#234C80" strokeWidth="3" strokeLinejoin="round" />
    <path d="M53 26V40" stroke="#D49C29" strokeWidth="3" strokeLinecap="round" />
    <circle cx="53" cy="43" r="3" fill="#D49C29" />
  </svg>
);
const ExamCardImage = ({ exam }) => {
  const [failed, setFailed] = React.useState(false);
  const src = getExamImage(exam);
  React.useEffect(() => setFailed(false), [src]);
  return src && !failed ? (
    <img
      src={src}
      alt={`${getExamName(exam)} logo`}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ width: 52, height: 52, borderRadius: 10, objectFit: "contain", display: "block" }}
    />
  ) : <ExamFallbackArt />;
};
const EyeIcon = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>;
const resolveOptionId = (value, options) => {
  if (value === null || value === undefined || value === "") return "";
  const match = options.find(
    (option) =>
      String(option.id) === String(value) ||
      String(option.name).toLowerCase() === String(value).toLowerCase()
  );
  return match ? String(match.id) : String(value);
};
/* =========================================================
   FEES
========================================================= */
const DEFAULT_BOOK_CHARGE = 0;
const DEFAULT_QUESTION_SET_CHARGE = 0;
const DEFAULT_COURIER_CHARGE = 50;
/* =========================================================
   EMPTY FORM
========================================================= */
const emptyForm = {
  parentName: "",
  parentMobile: "",
  parentEmail: "",
  /* Student */
  name: "",
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  /* Academic */
  class: "",
  schoolId: "",
  state: "Maharashtra",
  district: "",
  taluka: "",
  status: "active",
  medium: "",
  /* Personal */
  gender: "",
  dob: "",
  altPhone: "",
  address: "",
  pincode: "",
  /* Examination */
  standard: "",
  exams: [],
  /* Books / Courier */
  materials: {},
  deliveryMethod: "courier",
  useStudentAddress: true,
  courierAddress: {
    addressLine1: "",
    village: "",
    district: "",
    pincode: "",
  },
  /* Payment */
  paymentStatus: "pending",
  paymentMethod: "",
  paymentId: "",
};
/* =========================================================
   HELPERS
========================================================= */
const getExamId = (exam) => {
  if (
    typeof exam === "string" ||
    typeof exam === "number"
  ) {
    return exam;
  }
  return (
    exam?.id ??
    exam?.Id ??
    exam?.examId ??
    exam?.ExamId ??
    exam?.value ??
    exam?.Value
  );
};
const getExamName = (exam) => {
  if (typeof exam === "string") {
    return exam;
  }
  return (
    exam?.name ||
    exam?.Name ||
    exam?.examName ||
    exam?.ExamName ||
    exam?.label ||
    exam?.Label ||
    exam?.title ||
    exam?.Title ||
    `Exam ${getExamId(exam) ?? ""}`
  );
};
/*
  Exam fee can be stored using different property names.
*/
const getExamFee = (exam) => {
  if (!exam || typeof exam === "string") {
    return 0;
  }
  const fee =
    exam?.fee ??
    exam?.Fee ??
    exam?.fees ??
    exam?.Fees ??
    exam?.amount ??
    exam?.Amount ??
    exam?.price ??
    exam?.Price ??
    exam?.examFee ??
    exam?.ExamFee ??
    exam?.registrationFee ??
    exam?.RegistrationFee ??
    0;
  const numericFee = Number(fee);
  return Number.isNaN(numericFee)
    ? 0
    : numericFee;
};
/*
  Gets possible class/standard values from exam.
*/
const isStandardRelationKey = (key) => {
  const normalized = String(key || "").toLowerCase();
  /*
    IMPORTANT:
    ExamTypeId identifies Medium; StdStatusId is sent as 0.
    Never treat StdStatusId / Status / Medium fields as Standard.
  */
  if (
    normalized.includes("status") ||
    normalized.includes("medium")
  ) {
    return false;
  }
  return (
    normalized.includes("std") ||
    normalized.includes("standard") ||
    normalized.includes("class") ||
    normalized.includes("grade")
  );
};
const normalizeStandardCompareValues = (value) => {
  const output = new Set();
  const add = (input) => {
    if (input === null || input === undefined || input === "") return;
    if (Array.isArray(input)) {
      input.forEach(add);
      return;
    }
    if (typeof input === "object") {
      Object.entries(input).forEach(([key, child]) => {
        if (isStandardRelationKey(key)) {
          add(child);
        }
      });
      return;
    }
    const raw = String(input).trim().toLowerCase();
    if (!raw) return;
    raw
      .split(/[,|;/]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .forEach((part) => {
        output.add(part);
        // "5th", "Std 5", "Standard 5" -> "5"
        const numericMatch = part.match(/\d+/);
        if (numericMatch) {
          output.add(String(Number(numericMatch[0])));
        }
      });
  };
  add(value);
  return Array.from(output);
};
const getExamStandards = (exam) => {
  if (!exam || typeof exam === "string") return [];
  /*
    STRICT STANDARD RELATION ONLY.
    Do not use StdStatusId here; Medium is identified by ExamTypeId.
  */
  const directValues = [
    exam?.stdId,
    exam?.StdId,
    exam?.standardId,
    exam?.StandardId,
    exam?.standardMasterId,
    exam?.StandardMasterId,
    exam?.stdMasterId,
    exam?.StdMasterId,
    exam?.classId,
    exam?.ClassId,
    exam?.stdIds,
    exam?.StdIds,
    exam?.standardIds,
    exam?.StandardIds,
    exam?.classIds,
    exam?.ClassIds,
    exam?.stdName,
    exam?.StdName,
    exam?.standardName,
    exam?.StandardName,
    exam?.standardMasterName,
    exam?.StandardMasterName,
    exam?.className,
    exam?.ClassName,
    exam?.class,
    exam?.Class,
    exam?.classes,
    exam?.Classes,
    exam?.standard,
    exam?.Standard,
    exam?.standardMaster,
    exam?.StandardMaster,
    exam?.standards,
    exam?.Standards,
    exam?.std,
    exam?.Std,
    exam?.grade,
    exam?.Grade,
  ];
  const values = normalizeStandardCompareValues(directValues);
  /*
    Fallback for custom DTO names, but still STRICT:
    keys containing Status/Medium are explicitly excluded.
  */
  if (values.length === 0 && typeof exam === "object") {
    Object.entries(exam).forEach(([key, value]) => {
      if (isStandardRelationKey(key)) {
        normalizeStandardCompareValues(value).forEach((x) => {
          if (!values.includes(x)) values.push(x);
        });
      }
    });
  }
  return values;
};
/*
  Gets possible medium values from exam.
*/
const getExamMediums = (exam) => {
  if (!exam || typeof exam === "string") return [];
  const value =
    exam?.examTypeId ?? exam?.ExamTypeId ??
    exam?.medium ?? exam?.Medium ??
    exam?.mediums ?? exam?.Mediums ??
    exam?.language ?? exam?.Language ??
    exam?.languages ?? exam?.Languages;
  const values = Array.isArray(value) ? value :
    value !== undefined && value !== null && value !== "" ? [value] : [];
  return values.flatMap((x) => {
    if (x && typeof x === "object") {
      return [
        pickValue(x, ["examTypeId", "ExamTypeId", "id", "Id"]),
        pickValue(x, ["typeName", "TypeName", "examTypeName", "ExamTypeName", "name", "Name", "medium", "Medium"]),
      ].filter((v) => v !== null && v !== undefined && v !== "").map(String);
    }
    return [String(x)];
  });
};
/*
  Find exam from API exam list.
*/
const findExam = (examId, exams = []) => {
  return exams.find(
    (exam) =>
      String(getExamId(exam)) ===
      String(examId)
  );
};
/*
  Format INR.
*/
const formatINR = (amount) => {
  if (
    amount === null ||
    amount === undefined ||
    Number.isNaN(Number(amount))
  ) {
    return "₹0";
  }
  return `₹${new Intl.NumberFormat(
    "en-IN"
  ).format(Math.round(Number(amount)))}`;
};
/* =========================================================
   INITIAL FORM
========================================================= */
const getGenderLabel = (value) => {
  if (
    value === 0 ||
    value === "0" ||
    String(value).toLowerCase() === "male"
  ) {
    return "Male";
  }
  if (
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "female"
  ) {
    return "Female";
  }
  if (
    value === 2 ||
    value === "2" ||
    String(value).toLowerCase() === "other"
  ) {
    return "Other";
  }
  return "";
};
const getDateInputValue = (value) => {
  if (!value) return "";
  return String(value).substring(
    0,
    10
  );
};
const getInitialExamIds = (
  initial
) => {
  let details =
    initial?.studentDetails ??
    initial?.StudentDetails ??
    [];
  if (typeof details === "string") {
    try {
      details =
        JSON.parse(details);
    } catch {
      details = [];
    }
  }
  if (
    Array.isArray(details) &&
    details.length > 0
  ) {
    return details
      .filter((detail) => {
        const active =
          detail?.isActive ??
          detail?.IsActive;
        return (
          active === undefined ||
          active === null ||
          active === true ||
          active === 1 ||
          active === "1"
        );
      })
      .map(
        (detail) =>
          detail?.examId ??
          detail?.ExamId
      )
      .filter(
        (id) =>
          id !== null &&
          id !== undefined &&
          id !== ""
      )
      .filter((id, index, ids) => ids.findIndex(x => String(x) === String(id)) === index);
  }
  if (
    Array.isArray(
      initial?.selectedExams
    )
  ) {
    return initial.selectedExams
      .map(
        (exam) =>
          exam?.examId ??
          exam?.ExamId ??
          exam?.id ??
          exam?.Id
      )
      .filter(
        (id) =>
          id !== null &&
          id !== undefined
      );
  }
  if (
    Array.isArray(initial?.exams)
  ) {
    return initial.exams;
  }
  return [];
};
const getInitialForm = (initial) => {
  if (!initial) {
    return {
      ...emptyForm,
      courierAddress: {
        ...emptyForm.courierAddress,
      },
      materials: {},
      selectedBookIds: {},
      exams: [],
    };
  }
  const isActive =
    initial?.isActive ??
    initial?.IsActive;
  return {
    ...emptyForm,
    ...initial,
    /* Parents */
    parentName:
      initial?.parentName ??
      initial?.ParentName ??
      initial?.fatherName ??
      initial?.FatherName ??
      initial?.parent?.name ??
      initial?.parent?.parentName ??
      "",
    parentMobile:
      initial?.parentMobile ??
      initial?.ParentMobile ??
      initial?.parentPhone ??
      initial?.fatherPhone ??
      initial?.FatherPhone ??
      initial?.parent?.mobile ??
      initial?.parent?.phone ??
      "",
    parentEmail:
      initial?.parentEmail ??
      initial?.ParentEmail ??
      initial?.fatherEmail ??
      initial?.FatherEmail ??
      initial?.parent?.email ??
      "",
    /* Student */
    name:
      initial?.name ??
      initial?.Name ??
      initial?.fullName ??
      initial?.FullName ??
      "",
    firstName:
      initial?.firstName ??
      initial?.FirstName ??
      initial?.student?.firstName ??
      "",
    middleName:
      initial?.middleName ??
      initial?.MiddleName ??
      initial?.student?.middleName ??
      "",
    lastName:
      initial?.lastName ??
      initial?.LastName ??
      initial?.student?.lastName ??
      "",
    email:
      initial?.email ??
      initial?.Email ??
      initial?.student?.email ??
      "",
    phone:
      initial?.phone ??
      initial?.Phone ??
      initial?.mobileNo ??
      initial?.MobileNo ??
      initial?.student?.mobile ??
      "",
    /* Academic */
    class:
      initial?.stdId ??
      initial?.StdId ??
      initial?.class ??
      initial?.Class ??
      initial?.student?.currentStandardId ??
      initial?.student?.currentStandard ??
      "",
    state: "Maharashtra",
    district: initial?.district ?? initial?.District ?? initial?.districtId ?? initial?.DistrictId ?? "",
    taluka: initial?.taluka ?? initial?.Taluka ?? initial?.talukaId ?? initial?.TalukaId ?? "",
    schoolId:
      initial?.schoolId ??
      initial?.SchoolId ??
      initial?.student?.schoolId ??
      "",
    status:
      initial?.status ??
      initial?.Status ??
      (
        isActive === false ||
          isActive === 0 ||
          isActive === "0"
          ? "dropped"
          : "active"
      ),
    medium:
      initial?.examTypeId ??
      initial?.ExamTypeId ??
      initial?.student?.examTypeId ??
      initial?.student?.ExamTypeId ??
      initial?.examSelection?.examTypeId ??
      initial?.medium ??
      initial?.Medium ??
      "",
    /* Personal */
    gender:
      getGenderLabel(
        initial?.gender ??
        initial?.Gender ??
        initial?.student?.gender
      ),
    dob:
      getDateInputValue(
        initial?.dob ??
        initial?.DOB ??
        initial?.student?.dateOfBirth
      ),
    altPhone:
      initial?.altPhone ??
      initial?.AltPhone ??
      initial?.student?.alternatePhone ??
      "",
    address:
      initial?.address ??
      initial?.Address ??
      initial?.student?.address?.addressLine1 ??
      "",
    pincode:
      initial?.pincode ??
      initial?.Pincode ??
      initial?.student?.address?.pincode ??
      "",
    /* Exam */
    standard:
      initial?.stdId ??
      initial?.StdId ??
      initial?.standard ??
      initial?.examSelection?.standard ??
      initial?.class ??
      "",
    exams:
      getInitialExamIds(initial),
    materials:
      initial?.materials || {},
    selectedBookIds: initial?.selectedBookIds || {},
    /* Courier */
    courierAddress: {
      ...emptyForm.courierAddress,
      ...(initial?.courierAddress || {}),
      ...(initial?.delivery?.courierAddress || {}),
    },
    deliveryMethod:
      initial?.deliveryMethod ??
      initial?.delivery?.method ??
      "courier",
    useStudentAddress:
      initial?.useStudentAddress ??
      initial?.delivery?.useStudentAddress ??
      true,
    /* Payment */
    paymentStatus:
      initial?.paymentStatus ??
      initial?.payment?.status ??
      "pending",
    paymentMethod:
      initial?.paymentMethod ??
      initial?.payment?.method ??
      "",
    paymentId:
      initial?.paymentId ??
      initial?.payment?.paymentId ??
      "",
  };
};
/* =========================================================
   FIELD ERROR
========================================================= */
const FieldError = ({
  name,
  errors,
}) => {
  if (!errors?.[name]) {
    return null;
  }
  return (
    <div
      style={{
        color: "#dc2626",
        fontSize: 10.5,
        marginTop: 4,
      }}
    >
      {errors[name]}
    </div>
  );
};
/* =========================================================
   INPUT
========================================================= */
const Input = ({
  label,
  name,
  value,
  onChange,
  errors,
  required = false,
  type = "text",
  placeholder = "",
  ...props
}) => {
  return (
    <div className="fg">
      <label className="fl">
        {label}
        {required ? " *" : ""}
      </label>
      <input
        className="fi"
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        {...props}
      />
      <FieldError
        name={name}
        errors={errors}
      />
    </div>
  );
};
/* =========================================================
   SELECT
========================================================= */
const Select = ({
  label,
  name,
  value,
  onChange,
  errors,
  required = false,
  children,
  ...props
}) => {
  return (
    <div className="fg">
      <label className="fl">
        {label}
        {required ? " *" : ""}
      </label>
      <select
        className="fi"
        name={name}
        value={value ?? ""}
        onChange={onChange}
        {...props}
      >
        {children}
      </select>
      <FieldError
        name={name}
        errors={errors}
      />
    </div>
  );
};
const SearchableSchoolSelect = ({ options, value, onChange, disabled, placeholder, error }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const selected = options.find((item) => String(item.id) === String(value));
  const matches = options.filter((item) => String(item.name || "").normalize("NFC").toLocaleLowerCase().includes(query.trim().normalize("NFC").toLocaleLowerCase()));
  useEffect(() => { setOpen(false); setQuery(""); setActive(0); }, [value, disabled]);
  const choose = (item) => { onChange(String(item.id)); setOpen(false); setQuery(""); };
  return (
    <div className="fg" style={{ position: "relative" }} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) { setOpen(false); setQuery(""); }
    }}>
      <label className="fl" htmlFor="school-combobox">School / शाळा *</label>
      <div style={{ position: "relative" }}>
        <input
          id="school-combobox" className="fi" role="combobox" autoComplete="off"
          aria-expanded={open && !disabled} aria-controls="school-options" aria-autocomplete="list"
          aria-activedescendant={open && matches[active] ? `school-option-${active}` : undefined}
          aria-invalid={Boolean(error)} aria-describedby={error ? "school-error" : undefined}
          disabled={disabled} placeholder={placeholder}
          value={open ? query : selected?.name || ""}
          style={{ width: "100%", paddingRight: 28 }}
          onFocus={() => { setOpen(true); setQuery(""); setActive(0); }}
          onClick={() => setOpen(true)}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); setActive(0); }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault(); setOpen(true);
              setActive((index) => !open ? 0 : Math.max(0, Math.min(matches.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
            } else if (event.key === "Enter" && open) {
              event.preventDefault(); if (matches[active]) choose(matches[active]);
            } else if (event.key === "Escape") { event.preventDefault(); setOpen(false); setQuery(""); }
          }}
        />
        <span aria-hidden="true" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>▾</span>
      </div>
      {open && !disabled && (
        <div id="school-options" role="listbox" aria-label="Schools" style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, maxHeight: 240, overflowY: "auto", background: "white", color: "#1f2937", border: "1px solid #d1d5db", borderRadius: 8, boxShadow: "0 6px 18px #0002" }}>
          {matches.length ? matches.map((item, index) => (
            <div id={`school-option-${index}`} key={item.id} role="option" aria-selected={String(item.id) === String(value)}
              ref={(element) => { if (element && index === active) element.scrollIntoView({ block: "nearest" }); }}
              onMouseDown={(event) => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(item)}
              style={{ padding: "10px 12px", cursor: "pointer", background: index === active ? "#eff6ff" : "white" }}>
              {item.name}{String(item.id) === String(value) ? " ✓" : ""}
            </div>
          )) : <div role="status" style={{ padding: 12 }}>शाळा सापडली नाही.</div>}
        </div>
      )}
      {error && <div id="school-error" style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
};
export default function StudentForm({
  title,
  initial,
  schools = [],
  districtMasters = [],
  talukaMasters = [],
  onBack,
  onSave,
  onRegistrationComplete,
  publicRegistration = false,
  addStudentUrl =
  "Student/addStudent",
  updateStudentUrl =
  "Student/updateStudent",
  standardUrl =
  "StandardMaster/getAllStandardMasters",
  mediumUrl =
  "ExamTypes/getAllExamTypes",
  schoolUrl =
  "School/getAllSchool",
  examUrl =
  "Exam/getAllExam",
  authToken = "",
  loggedInTeacherId,
  loggedInUserId,
  onStudentMasterSaved,
  onStartPayment,
}) {
  const storedUser = JSON.parse(
    sessionStorage.getItem("user") || "{}"
  );
  const currentUserId =
    loggedInUserId ??
    sessionStorage.getItem("userId") ??
    storedUser?.userId ??
    storedUser?.UserId ??
    storedUser?.id ??
    storedUser?.Id;
  const currentTeacherId =
    loggedInTeacherId ??
    sessionStorage.getItem("teacherId") ??
    storedUser?.teacherId ??
    storedUser?.TeacherId ??
    0;
  const currentUsersId = publicRegistration
    ? 0
    : currentUserId;
  const currentTeachersId = publicRegistration
    ? 0
    : currentTeacherId;
  const [step, setStep] =
    useState(0);
  const [f, setF] = useState(() =>
    getInitialForm(initial)
  );
  const [errors, setErrors] =
    useState({});
  const [
    paymentProcessing,
    setPaymentProcessing,
  ] = useState(false);
  const [savedStudentMaster, setSavedStudentMaster] =
    useState(initial || null);
  const [registrationConfirmOpen, setRegistrationConfirmOpen] = useState(false);
  const [standardOptions, setStandardOptions] =
    useState([]);
  const [mediumOptions, setMediumOptions] =
    useState([]);
  const [schoolOptions, setSchoolOptions] =
    useState(() => (schools || []).map(normalizeSchool).filter((x) => x.id !== null && x.id !== undefined));
  const [examOptions, setExamOptions] =
    useState([]);
  const [bookMasters, setBookMasters] = useState([]);
  const [bookLoading, setBookLoading] = useState(false);
  const [bookError, setBookError] = useState("");
  useEffect(() => {
    let cancelled = false;
    const loadBooks = async () => {
      setBookLoading(true);
      setBookError("");
      try {
        const all = [];
        const pageSize = 100;
        for (let page = 1; page <= 100; page++) {
          const response = await apiService.post("BookMaster/getPaginatedBookMasters", {
            Page: page, PageSize: pageSize, SearchTerm: "",
            SortDirection: "DESC", SortField: "Id",
            IncludeInActive: false, IsActive: true,
          });
          if (response.data?.isSuccess === false) {
            throw new Error(response.data?.failureReason || "Book Master API failed");
          }
          const output = response.data?.output ?? response.data?.Output ?? {};
          const rows = output.result ?? output.Result ?? [];
          if (!Array.isArray(rows)) throw new Error("Invalid Book Master API response");
          // Paginated response may omit teaBooAssDetlst; retrieve full master details.
          const fullRows = await Promise.all(rows.map(async (row) => {
            const detailList = row.teaBooAssDetlst ?? row.TeaBooAssDetlst;
            if (Array.isArray(detailList) && detailList.length) return row;
            const masterId = Number(row.id ?? row.Id);
            if (!(masterId > 0)) return row;
            try {
              const full = await apiService.get(`BookMaster/getBookMasterById/${masterId}`);
              const content = full.data?.output ?? full.data?.Output ?? full.data?.data ?? full.data;
              const detail = content?.bookMaster ?? content?.BookMaster ?? content;
              if (!detail || typeof detail !== "object") return row;
              return { ...row, ...detail };
            } catch (error) {
              console.warn("Unable to load book details for master", masterId, error);
              return row;
            }
          }));
          all.push(...fullRows);
          if (rows.length < pageSize) break;
          if (page === 100) throw new Error("Too many Book Master pages; contact administrator");
        }
        if (!cancelled) setBookMasters(all);
      } catch (e) {
        if (!cancelled) setBookError(getApiErrorMessage(e, "Unable to load books"));
      } finally {
        if (!cancelled) setBookLoading(false);
      }
    };
    loadBooks();
    return () => { cancelled = true; };
  }, []);
  const [viewExam, setViewExam] = useState(null);
  const [districtList, setDistrictList] = useState([]);
  const [talukaList, setTalukaList] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationError, setLocationError] = useState("");
  const [addDistrictOpen, setAddDistrictOpen] = useState(false);
  const [addTalukaOpen, setAddTalukaOpen] = useState(false);
  const [newDistrict, setNewDistrict] = useState("");
  const [newTaluka, setNewTaluka] = useState("");
  const [districtSaving, setDistrictSaving] = useState(false);
  const [talukaSaving, setTalukaSaving] = useState(false);
  const [lookupLoading, setLookupLoading] =
    useState(true);
  const [lookupError, setLookupError] =
    useState("");
  const DISTRICT_URL = "DistrictMaster/getAllDistrictMasters?includeInActive=true";
  const TALUKA_URL = "TalukaMaster/getAllTalukaMasters?includeInActive=true";
  const normalizeDistrictRow = row => ({
    id: Number(row.id ?? row.Id ?? row.districtId ?? row.DistrictId ?? row.districtMasterId ?? row.DistrictMasterId ?? 0),
    name: String(row.districtName ?? row.DistrictName ?? row.districtMasterName ?? row.DistrictMasterName ?? row.name ?? row.Name ?? ""),
    stateId: Number(row.stateId ?? row.StateId ?? row.stateMasterId ?? row.StateMasterId ?? 0),
    isActive: row.isActive ?? row.IsActive ?? true,
  });
  const normalizeTalukaRow = row => ({
    id: Number(row.id ?? row.Id ?? row.talukaId ?? row.TalukaId ?? row.talukaMasterId ?? row.TalukaMasterId ?? 0),
    name: String(row.talukaName ?? row.TalukaName ?? row.talukaMasterName ?? row.TalukaMasterName ?? row.name ?? row.Name ?? ""),
    districtId: Number(row.districtId ?? row.DistrictId ?? row.districtMasterId ?? row.DistrictMasterId ?? 0),
    isActive: row.isActive ?? row.IsActive ?? true,
  });
  const fetchDistrictList = async () => {
    const response = await apiService.get(DISTRICT_URL);
    if (response.data?.isSuccess === false) throw new Error(response.data.failureReason || "District API failed");
    const list = extractApiList(response.data).map(normalizeDistrictRow).filter(x => x.id > 0 && x.name && x.isActive !== false && x.isActive !== 0);
    setDistrictList(list);
    return list;
  };
  const fetchTalukaList = async () => {
    const response = await apiService.get(TALUKA_URL);
    if (response.data?.isSuccess === false) throw new Error(response.data.failureReason || "Taluka API failed");
    const list = extractApiList(response.data).map(normalizeTalukaRow).filter(x => x.id > 0 && x.name && x.isActive !== false && x.isActive !== 0);
    setTalukaList(list);
    return list;
  };
  useEffect(() => {
    let live = true;
    (async () => {
      setLocationsLoading(true);
      const results = await Promise.allSettled([fetchDistrictList(), fetchTalukaList()]);
      if (!live) return;
      const errors = results.filter(x => x.status === "rejected").map(x => x.reason?.message || "Location load failed");
      setLocationError(errors.join(" | "));
      setLocationsLoading(false);
    })();
    return () => { live = false; };
  }, []);
  const addDistrict = async () => {
    const name = newDistrict.trim();
    if (!name) return showNotification("error", "Enter district name");
    try {
      setDistrictSaving(true);
      const response = await apiService.post("DistrictMaster/addDistrictMaster", { districtName: name, stateId: 1, isActive: 1 });
      const output = response.data?.output ?? response.data?.Output ?? {};
      if (response.data?.isSuccess === false || /duplicate/i.test(String(output.status ?? output.Status ?? ""))) throw new Error(output.message ?? output.Message ?? response.data?.failureReason ?? "District could not be added");
      const refreshed = await fetchDistrictList();
      const match = refreshed.find(d => d.name.toLowerCase() === name.toLowerCase());
      if (match) setF(prev => ({ ...prev, district: String(match.id), taluka: "", schoolId: "" }));
      setNewDistrict(""); setAddDistrictOpen(false);
      showNotification("success", "District added successfully");
    } catch (e) { showNotification("error", getApiErrorMessage(e, e.message || "Add District failed")); }
    finally { setDistrictSaving(false); }
  };
  const addTaluka = async () => {
    const name = newTaluka.trim();
    if (!f.district) return showNotification("error", "Select district first");
    if (!name) return showNotification("error", "Enter taluka name");
    try {
      setTalukaSaving(true);
      const response = await apiService.post("TalukaMaster/addTalukaMaster", { talukaName: name, districtId: Number(f.district), isActive: 1 });
      const output = response.data?.output ?? response.data?.Output ?? {};
      if (response.data?.isSuccess === false || /duplicate/i.test(String(output.status ?? output.Status ?? ""))) throw new Error(output.message ?? output.Message ?? response.data?.failureReason ?? "Taluka could not be added");
      const refreshed = await fetchTalukaList();
      const match = refreshed.find(t => t.districtId === Number(f.district) && t.name.toLowerCase() === name.toLowerCase());
      if (match) setF(prev => ({ ...prev, taluka: String(match.id), schoolId: "" }));
      setNewTaluka(""); setAddTalukaOpen(false);
      showNotification("success", "Taluka added successfully");
    } catch (e) { showNotification("error", getApiErrorMessage(e, e.message || "Add Taluka failed")); }
    finally { setTalukaSaving(false); }
  };
  const getTodayDate = () => {
    const today = new Date();
    const year =
      today.getFullYear();
    const month =
      String(
        today.getMonth() + 1
      ).padStart(2, "0");
    const day =
      String(
        today.getDate()
      ).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    const controller = new AbortController();
    const fetchList = async (
      url,
      label
    ) => {
      try {
        const response =
          await apiService.get(url);
        const data =
          response?.data;
        if (
          data?.isSuccess === false ||
          data?.IsSuccess === false
        ) {
          throw new Error(
            data?.failureReason ||
            data?.FailureReason ||
            data?.message ||
            data?.Message ||
            `${label} API failed`
          );
        }
        return extractApiList(
          data
        );
      } catch (error) {
        throw new Error(
          getApiErrorMessage(
            error,
            `${label} API failed`
          )
        );
      }
    };
    const loadLookups = async () => {
      setLookupLoading(true);
      setLookupError("");
      const results = await Promise.allSettled([
        fetchList(standardUrl, "Class"),
        fetchList(mediumUrl, "Medium"),
        fetchList(schoolUrl, "School"),
        fetchList(examUrl, "Exam"),
      ]);
      if (controller.signal.aborted) return;
      const errorsFound = [];
      let standards = [];
      let mediums = [];
      let fetchedSchools = [];
      let exams = [];
      if (results[0].status === "fulfilled") {
        standards = results[0].value
          .filter(isActiveItem)
          .map(normalizeStandard)
          .filter(
            (x) =>
              x.id !== null &&
              x.id !== undefined
          );
        setStandardOptions(standards);
      } else {
        errorsFound.push(
          results[0].reason?.message ||
          "Class API failed"
        );
      }
      if (results[1].status === "fulfilled") {
        mediums = results[1].value
          .filter(isActiveItem)
          .map(normalizeMedium)
          .filter(
            (x) =>
              x.id !== null &&
              x.id !== undefined
          );
        setMediumOptions(mediums);
      } else {
        errorsFound.push(
          results[1].reason?.message ||
          "Medium API failed"
        );
      }
      if (results[2].status === "fulfilled") {
        fetchedSchools = results[2].value
          .filter((school) => {
            const active = school?.isActive ?? school?.IsActive;
            return active === undefined || active === null ||
              (active !== false && active !== 0 && active !== "0" && String(active).toLowerCase() !== "false");
          })
          .map(normalizeSchool)
          .filter(
            (x) =>
              x.id !== null &&
              x.id !== undefined
          );
        console.info("StudentForm school lookup:", {
          endpoint: schoolUrl,
          count: fetchedSchools.length,
          schoolLocations: fetchedSchools.slice(0, 10).map(x => ({
            schoolId: x.id,
            schoolName: x.name,
            stateId: x.raw?.stateId ?? x.raw?.StateId,
            districtId: x.raw?.districtId ?? x.raw?.DistrictId,
            talukaId: x.raw?.talukaId ?? x.raw?.TalukaId
          }))
        });
        // Avoid discarding a supplied schools prop if the getAllSchool endpoint
        // returns no rows while the SchoolPage paginated endpoint has data.
        setSchoolOptions(fetchedSchools.length ? fetchedSchools :
          (schools || []).map(normalizeSchool).filter(x => x.id !== null && x.id !== undefined));
      } else {
        errorsFound.push(
          results[2].reason?.message ||
          "School API failed"
        );
      }
      if (results[3].status === "fulfilled") {
        exams = results[3].value
          .filter(isActiveItem)
          .map(normalizeExam)
          .filter(
            (x) =>
              x.id !== null &&
              x.id !== undefined
          );
        setExamOptions(exams);
      } else {
        errorsFound.push(
          results[3].reason?.message ||
          "Exam API failed"
        );
      }
      /*
        Edit-mode compatibility: if existing value contains a name
        (for example class = "5"), convert it to the API ID.
      */
      setF((prev) => ({
        ...prev,
        ...(() => {
          const savedSchool = fetchedSchools.find(x => String(x.id) === String(prev.schoolId));
          return savedSchool ? {
            state: "Maharashtra",
            district: prev.district || String(locationValue(savedSchool, "district") || ""),
            taluka: prev.taluka || String(locationValue(savedSchool, "taluka") || "")
          } : {};
        })(),
        class:
          standards.length
            ? resolveOptionId(
              prev.class,
              standards
            )
            : prev.class,
        medium:
          mediums.length
            ? resolveOptionId(
              prev.medium,
              mediums
            )
            : prev.medium,
        schoolId:
          fetchedSchools.length
            ? resolveOptionId(
              prev.schoolId,
              fetchedSchools
            )
            : prev.schoolId,
        /*
          Edit mode can receive exam IDs or exam names.
          Convert existing values to master Exam IDs after lookup load.
        */
        exams:
          (prev.exams || []).map(
            (value) => {
              const match =
                exams.find(
                  (exam) =>
                    String(exam.id) ===
                    String(value) ||
                    String(
                      exam.name
                    ).toLowerCase() ===
                    String(
                      value
                    ).toLowerCase()
                );
              return match
                ? match.id
                : value;
            }
          ),
      }));
      setLookupError(errorsFound.join(" | "));
      setLookupLoading(false);
    };
    loadLookups().catch((error) => {
      if (!controller.signal.aborted) {
        setLookupError(error?.message || "Unable to load academic data");
        setLookupLoading(false);
      }
    });
    return () => controller.abort();
  }, [standardUrl, mediumUrl, schoolUrl, examUrl, authToken]);
  const isEdit =
    Boolean(initial);
  /*
    ADD MODE:
    Personal -> Academic -> Books -> save -> Review -> Payment
    EDIT MODE:
    Personal -> Academic -> Books -> Review
    Payment step is intentionally hidden while editing.
  */
  const visibleSteps =
    isEdit
      ? STEPS.filter(
        (item) =>
          item.id !== "payment" && item.id !== "courier"
      )
      : STEPS;
  const currentStep =
    visibleSteps[step];
  /* =======================================================
     SET FIELD
  ======================================================= */
  const s = (key, value) => {
    setF((prev) => ({
      ...prev,
      [key]: value,
    }));
    setErrors((prev) => {
      const updated = {
        ...prev,
      };
      delete updated[key];
      return updated;
    });
  };
  /* =======================================================
     SET COURIER
  ======================================================= */
  const setCourier = (
    key,
    value
  ) => {
    setF((prev) => ({
      ...prev,
      courierAddress: {
        ...prev.courierAddress,
        [key]: value,
      },
    }));
    setErrors((prev) => {
      const updated = {
        ...prev,
      };
      delete updated[
        `courier.${key}`
      ];
      return updated;
    });
  };
  /* =======================================================
     FILTER EXAMS
  ======================================================= */
  const selectedStandard = useMemo(
    () => standardOptions.find((x) => String(x.id) === String(f.class)) || null,
    [standardOptions, f.class]
  );
  const selectedMedium = useMemo(
    () => mediumOptions.find((x) => String(x.id) === String(f.medium)) || null,
    [mediumOptions, f.medium]
  );
  // Master APIs contain labels; School API contains their corresponding IDs.
  const districtOptions = useMemo(() => districtList.filter(d => !d.stateId || d.stateId === 1), [districtList]);
  const talukaOptions = useMemo(() => talukaList.filter(t => String(t.districtId) === String(f.district)), [talukaList, f.district]);
  const filteredSchools = useMemo(() => schoolOptions.filter(school => {
    const raw = school.raw || school;
    return f.district && f.taluka &&
      String(raw.districtId ?? raw.DistrictId ?? "") === String(f.district) &&
      String(raw.talukaId ?? raw.TalukaId ?? "") === String(f.taluka) &&
      String(raw.stateId ?? raw.StateId ?? 1) === "1";
  }), [schoolOptions, f.district, f.taluka]);
  const hasSchoolLocationData = true;
  const selectedSchool = useMemo(
    () => schoolOptions.find((x) => String(x.id) === String(f.schoolId)) || null,
    [schoolOptions, f.schoolId]
  );
  const classLabel = selectedStandard?.name || f.class || "";
  const mediumLabel = selectedMedium?.name || f.medium || "";
  const availableExams = useMemo(() => {
    if (!f.class || !f.medium) return [];
    const selectedStandards = new Set(
      normalizeStandardCompareValues([f.class, classLabel])
    );
    const selectedMediums = new Set(
      [f.medium, mediumLabel].map((v) => String(v).trim().toLowerCase())
    );
    return examOptions.filter((exam) => {
      const standards = getExamStandards(exam);
      if (!standards.length || !standards.some((v) => selectedStandards.has(String(v).toLowerCase()))) {
        return false;
      }
      const mediums = getExamMediums(exam);
      // If exam master has no medium relation, do not hide its class-matched exam.
      return !mediums.length || mediums.some((v) => selectedMediums.has(String(v).trim().toLowerCase()));
    });
  }, [f.class, f.medium, classLabel, mediumLabel, examOptions]);
  /* =======================================================
     SELECTED EXAMS
  ======================================================= */
  const selectedExams = useMemo(() => {
    return (f.exams || []).map((examId) => {
      const exam = findExam(examId, examOptions);
      return {
        id: examId,
        name: exam ? getExamName(exam) : String(examId),
        fee: exam ? getExamFee(exam) : 0,
      };
    });
  }, [f.exams, examOptions]);
  const booksForExam = (exam) => {
    const clean = value => String(value ?? "").trim().toLocaleLowerCase();
    const examName = clean(exam.name);
    return bookMasters.flatMap(master => {
      if (master.isActive === false) return [];
      // competitiveExams may contain comma-separated exam names.
      const names = String(master.competitiveExams ?? master.CompetitiveExams ?? "")
        .split(/[,;|]/).map(clean).filter(Boolean);
      if (!names.includes(examName)) return [];
      const details = master.teaBooAssDetlst ?? master.TeaBooAssDetlst ?? [];
      return details.filter(book => book.isActive !== false && book.IsActive !== false)
        .map(book => ({
          examId: Number(exam.id),
          bookMasterId: Number(master.id ?? master.Id),
          bookDetailId: Number(book.id ?? book.Id),
          bookName: String(book.bookName ?? book.BookName ?? ""),
        })).filter(book => book.bookDetailId > 0 && book.bookName);
    });
  };
  // Automatically include the first two mapped book details per selected exam.
  // Keep edit-mode saved details managed by the existing detail preservation flow.
  const autoBooksForExam = (exam) => booksForExam(exam).slice(0, 2);
  /* =======================================================
     EXAM FEE TOTAL
  ======================================================= */
  const examFeeTotal =
    useMemo(() => {
      return selectedExams.reduce(
        (total, exam) =>
          total +
          Number(
            exam.fee || 0
          ),
        0
      );
    }, [selectedExams]);
  /* =======================================================
     FEE SUMMARY
  ======================================================= */
  const feeSummary =
    useMemo(() => {
      let materialTotal = 0;
      Object.entries(
        f.materials || {}
      ).forEach(
        ([examId, material]) => {
          if (
            !f.exams?.some(
              (id) =>
                String(id) ===
                String(examId)
            )
          ) {
            return;
          }
          if (
            material ===
            "book"
          ) {
            materialTotal +=
              DEFAULT_BOOK_CHARGE;
          }
          if (
            material ===
            "question-set"
          ) {
            materialTotal +=
              DEFAULT_QUESTION_SET_CHARGE;
          }
          if (
            material ===
            "book-question-set"
          ) {
            materialTotal +=
              DEFAULT_BOOK_CHARGE +
              DEFAULT_QUESTION_SET_CHARGE;
          }
        }
      );
      const courierCharge =
        f.deliveryMethod ===
          "courier"
          ? DEFAULT_COURIER_CHARGE * (f.exams?.length || 0)
          : 0;
      const finalAmount =
        examFeeTotal +
        materialTotal +
        courierCharge;
      return {
        examFeeTotal,
        materialTotal,
        courierCharge,
        finalAmount,
      };
    }, [
      f.exams,
      f.materials,
      f.deliveryMethod,
      examFeeTotal,
    ]);
  /* =======================================================
     VALIDATION - ACADEMIC
  ======================================================= */
  const validateAcademic =
    () => {
      const e = {};
      if (!f.class) {
        e.class =
          "Class is required";
      }
      if (!f.medium) {
        e.medium =
          "Medium is required";
      }
      if (!f.schoolId) {
        e.schoolId = "School is required";
      } else if (!filteredSchools.some(school => String(school.id) === String(f.schoolId))) {
        e.schoolId = "Select a school from the chosen taluka";
      }
      if (
        !isEdit && (
          !f.exams ||
          f.exams.length === 0
        )
      ) {
        e.exams =
          "Select at least one examination";
      }
      return e;
    };
  /* =======================================================
     VALIDATION - PERSONAL
  ======================================================= */
  const validatePersonal =
    () => {
      const e = {};
      /*
        IMPORTANT:
        Personal step currently displays only:
        First Name, Middle Name, Last Name,
        Gender, DOB, Pincode and Address.
        phone / altPhone / email are not shown on this step,
        so do not block Next because of hidden old values
        while editing a student.
      */
      if (
        !String(
          f.firstName ?? ""
        ).trim()
      ) {
        e.firstName =
          "First name is required";
      }
      if (
        !String(
          f.lastName ?? ""
        ).trim()
      ) {
        e.lastName =
          "Last name is required";
      }
      if (!f.gender) {
        e.gender =
          "Gender is required";
      }
      if (!f.dob) {
        e.dob =
          "Date of birth is required";
      } else if (
        f.dob > getTodayDate()
      ) {
        e.dob =
          "Future date is not allowed";
      }
      if (
        !String(
          f.parentMobile ?? ""
        ).trim()
      ) {
        e.parentMobile =
          "Parent mobile is required";
      } else if (
        !/^[0-9]{10}$/.test(
          String(f.parentMobile).trim()
        )
      ) {
        e.parentMobile =
          "Enter a valid 10 digit mobile number";
      }
      if (
        f.parentEmail &&
        !/^\S+@\S+\.\S+$/.test(
          String(f.parentEmail).trim()
        )
      ) {
        e.parentEmail =
          "Enter a valid email";
      }
      if (!f.state) e.state = "State is required";
      if (!f.district) e.district = "District is required";
      if (!f.taluka) e.taluka = "Taluka is required";
      if (
        f.pincode &&
        !/^[0-9]{6}$/.test(
          String(f.pincode)
        )
      ) {
        e.pincode =
          "Enter a valid 6 digit pincode";
      }
      return e;
    };
  /* =======================================================
     VALIDATION - COURIER
  ======================================================= */
  const validateCourier =
    () => {
      const e = {};
      if (
        f.deliveryMethod ===
        "courier"
      ) {
        if (
          f.useStudentAddress
        ) {
          if (
            !f.address.trim()
          ) {
            e.address =
              "Student address is required for courier delivery";
          }
          if (
            !/^[0-9]{6}$/.test(
              f.pincode || ""
            )
          ) {
            e.pincode =
              "Enter a valid 6 digit pincode";
          }
        } else {
          if (
            !f.courierAddress.addressLine1?.trim()
          ) {
            e[
              "courier.addressLine1"
            ] =
              "Courier address is required";
          }
          if (
            !f.courierAddress.village?.trim()
          ) {
            e[
              "courier.village"
            ] =
              "Village is required";
          }
          if (
            !f.courierAddress.district?.trim()
          ) {
            e[
              "courier.district"
            ] =
              "District is required";
          }
          if (
            !/^[0-9]{6}$/.test(
              f.courierAddress
                .pincode ||
              ""
            )
          ) {
            e[
              "courier.pincode"
            ] =
              "Enter a valid 6 digit pincode";
          }
        }
      }
      return e;
    };
  /* =======================================================
     VALIDATE CURRENT STEP
  ======================================================= */
  const validateStep =
    () => {
      switch (
      currentStep.id
      ) {
        case "academic":
          return validateAcademic();
        case "personal":
          return validatePersonal();
        case "review":
          return {};
        case "courier":
          return validateCourier();
        case "payment":
          return {};
        default:
          return {};
      }
    };
  /* =======================================================
     ADD STUDENT MASTER API
  ======================================================= */
  const getGenderId = (gender) => {
    /*
      Gender belongs to StudentMaster, NOT tbl_StudentDetails.
      Current mapping: Male = 0, Female = 1, Other = 2.
    */
    if (gender === "Female") return 1;
    if (gender === "Other") return 2;
    return 0;
  };
  // =======================================================
  // EXISTING STUDENT DETAILS
  // =======================================================
  const getExistingStudentDetails =
    () => {
      let details =
        savedStudentMaster?.studentDetails ??
        savedStudentMaster?.StudentDetails ??
        savedStudentMaster?.stdDetlst ??
        savedStudentMaster?.StdDetlst ??
        initial?.studentDetails ??
        initial?.StudentDetails ??
        initial?.stdDetlst ??
        initial?.StdDetlst ??
        [];
      if (typeof details === "string") {
        try {
          details =
            JSON.parse(details);
        } catch {
          details = [];
        }
      }
      return Array.isArray(details)
        ? details
        : [];
    };
  const toBoolean = (value) => {
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      String(value).toLowerCase() ===
      "true"
    );
  };
  // One StudentDetails row per exam; book IDs are comma-separated.
  // Book-wise courier/tracking rows are created separately in StudentBookSends.
  const getBookIdsForExam = (exam, savedDetails = getExistingStudentDetails()) => {
    const examId = Number(exam.id);
    const existingForExam = savedDetails.filter(
      (detail) => Number(detail.examId ?? detail.ExamId) === examId
    );
    const availableIds = new Set(booksForExam(exam).map(book => Number(book.bookDetailId)));
    const selected = (isEdit
      ? (f.selectedBookIds?.[String(exam.id)] || [])
      : autoBooksForExam(exam).map(book => book.bookDetailId))
      .map(Number)
      .filter(id => Number.isInteger(id) && id > 0 && availableIds.has(id));
    const previouslySaved = existingForExam.flatMap(detail =>
      String(detail.bookDetailId ?? detail.BookDetailId ?? "")
        .split(",").map(value => Number(value.trim()))
    ).filter(id => Number.isInteger(id) && id > 0);
    const ids = [...new Set(selected.length ? selected : previouslySaved)];
    if (!ids.length) {
      throw new Error(`Exam ${exam.name}: valid BookDetailId not found.`);
    }
    if (!isEdit && ids.length < 2) {
      throw new Error(`Exam ${exam.name}: two valid book details are required; found ${ids.length}.`);
    }
    return ids;
  };

  const buildStudentDetailsRequest = (studentId = 0) => {
    const existingDetails = getExistingStudentDetails();
    return selectedExams.map(exam => {
      const examId = Number(exam.id);
      const previousRows = existingDetails.filter(
        detail => Number(detail.examId ?? detail.ExamId) === examId
      );
      const previous = previousRows.find(detail =>
        Number(detail.examTotalFees ?? detail.ExamTotalFees ?? 0) > 0 ||
        Number(detail.feesPaid ?? detail.FeesPaid ?? 0) > 0 ||
        Number(detail.courierCharges ?? detail.CourierCharges ?? 0) > 0
      ) || previousRows[0];
      const bookIds = getBookIdsForExam(exam, existingDetails);
      const examTotalFees = Number(previous?.examTotalFees ?? previous?.ExamTotalFees ?? exam.fee ?? 0);
      // Preserve already-paid amounts for existing registrations.
      const feesPaid = previousRows.length
        ? previousRows.reduce((sum, row) => sum + Number(row.feesPaid ?? row.FeesPaid ?? 0), 0)
        : 0;
      const courierCharge = previousRows.length
        ? previousRows.reduce((sum, row) => sum + Number(row.courierCharges ?? row.CourierCharges ?? 0), 0)
        : (f.deliveryMethod === "courier" ? DEFAULT_COURIER_CHARGE : 0);
      const savedPending = previousRows.reduce(
        (sum, row) => sum + Number(row.pendingFees ?? row.PendingFees ?? 0), 0
      );
      const pendingFees = previousRows.length
        ? savedPending
        : Math.max(0, examTotalFees + courierCharge - feesPaid);
      return {
        Id: Number(previous?.id ?? previous?.Id ?? 0),
        StudentId: Number(studentId || previous?.studentId || previous?.StudentId || 0),
        ExamId: examId,
        ExamTypeId: Number(f.medium),
        BookDetailId: bookIds.join(","),
        // Tracking is updated later via StudentBookSend edit, not registration.
        PostTrackNo: "0",
        PostTrackDate: null,
        CourierCharges: courierCharge,
        IsBookSend: false,
        ExamTotalFees: examTotalFees,
        FeesPaid: feesPaid,
        PendingFees: pendingFees,
        IsPaid: pendingFees <= 0 && examTotalFees > 0,
        IsActive: previous?.isActive ?? previous?.IsActive ?? true,
      };
    });
  };

  // Insert a distinct StudentBookSends row for each book once student is created.
  // The backend should ideally save StudentMaster/Details/BookSends atomically.
  const addStudentBookSendRows = async (studentId, details) => {
    const createdBy = Number(currentUsersId ?? 0);
    for (const detail of details) {
      const bookIds = String(detail.BookDetailId).split(",").map(Number)
        .filter(id => Number.isInteger(id) && id > 0);
      for (const [index, bookId] of bookIds.entries()) {
        const payload = {
          StudentId: Number(studentId),
          ExamId: Number(detail.ExamId),
          BookDetailId: bookId,
          PostTrackNo: "0",
          PostTrackDate: null,
          CourierCharges: index === 0 ? Number(detail.CourierCharges || 0) : 0,
          IsBookSend: false,
          IsActive: true,
          CreatedBy: createdBy,
        };
        const result = await apiService.post("StudentBookSend/addStudentBookSend", payload);
        if (result?.data?.isSuccess === false || result?.data?.IsSuccess === false) {
          throw new Error(getApiErrorMessage({response: result}, `Book ${bookId} could not be saved`));
        }
      }
    }
  };
  const buildStudentMasterRequest = () => {
    const userId = Number(currentUsersId ?? 0);
    const teacherId = Number(currentTeachersId ?? 0);
    console.log("PUBLIC REGISTRATION:", publicRegistration);
    console.log("FINAL USER ID:", userId);
    console.log("FINAL TEACHER ID:", teacherId);

    if (!publicRegistration && !userId) {
      throw new Error("Logged-in UserId not found.");
    }
    return {
      DocumentNo:
        initial?.DocumentNo ||
        initial?.documentNo ||
        "",
      FirstName: f.firstName.trim(),
      MiddleName: f.middleName?.trim() || "",
      LastName: f.lastName.trim(),
      Gender: getGenderId(f.gender),
      DOB: f.dob,
      FatherName: f.parentName.trim(),
      FatherPhone: f.parentMobile.trim(),
      FatherEmail: f.parentEmail?.trim() || "",
      Address: f.address?.trim() || "",
      Pincode: f.pincode || "",
      StateId: 1,
      DistrictId: Number(f.district),
      TalukaId: Number(f.taluka),
      SchoolId: Number(f.schoolId),
      TeacherId: teacherId,
      UserId: userId,
      UserName: "",
      Password: "",
      CreatedBy: userId,
      StdId: Number(f.class),
      StdStatusId: 0,
      ExamTypeId: Number(f.medium),
      IsActive: true,
      StudentDetails:
        buildStudentDetailsRequest(0),
    };
  };
  const saveStudentMaster = async () => {
    const request = buildStudentMasterRequest();
    // Inspect this in DevTools Network > addStudent > Payload.
    console.info("STUDENT ADD TVP PAYLOAD CHECK", {
      createdBy: request.CreatedBy,
      deliveryMethod: f.deliveryMethod,
      reviewCourierCharge: feeSummary.courierCharge,
      studentDetails: request.StudentDetails.map(d => ({
        examId: d.ExamId,
        examTypeId: d.ExamTypeId,
        bookDetailId: d.BookDetailId,
        courierCharges: d.CourierCharges,
      })),
    });
    if (request.StudentDetails.some(d => !String(d.BookDetailId).split(",").every(id => Number.isInteger(Number(id)) && Number(id) > 0))) {
      throw new Error("Invalid BookDetailId: registration not submitted.");
    }
    if (f.deliveryMethod === "courier" && feeSummary.courierCharge > 0 &&
      request.StudentDetails.reduce((sum, d) => sum + Number(d.CourierCharges || 0), 0) !== feeSummary.courierCharge) {
      throw new Error("CourierCharges mismatch: registration not submitted.");
    }
    try {
      const response = await apiService.post(
        addStudentUrl,
        request
      );
      const responseData = response?.data;
      console.log(
        "ADD STUDENT RESPONSE:",
        responseData
      );
      console.log(
        "VALIDATION ERRORS:",
        responseData?.errors
      );
      const output =
        responseData?.output ??
        responseData?.Output;
      const outputStatus =
        output?.status ??
        output?.Status ??
        "";
      const outputMessage =
        output?.message ??
        output?.Message ??
        "";
      const statusText =
        String(outputStatus).toLowerCase();
      const messageText =
        String(outputMessage).toLowerCase();
      const isDuplicate =
        statusText === "duplicate_student" ||
        statusText.includes("duplicate") ||
        statusText.includes("already") ||
        (
          messageText.includes("already") &&
          messageText.includes("exist")
        );
      if (isDuplicate) {
        throw new Error(
          outputMessage ||
          "Student already exists."
        );
      }
      if (
        responseData?.isSuccess === false ||
        responseData?.IsSuccess === false
      ) {
        throw new Error(
          outputMessage ||
          responseData?.failureReason ||
          responseData?.FailureReason ||
          responseData?.message ||
          responseData?.Message ||
          "Unable to save student master"
        );
      }
      const createdStudentId = Number(
        output?.id ??
        output?.Id ??
        0
      );
      console.log(
        "CREATED STUDENT ID:",
        createdStudentId
      );
      if (
        !Number.isInteger(createdStudentId) ||
        createdStudentId <= 0
      ) {
        console.error(
          "Invalid StudentId in API response:",
          responseData
        );
        throw new Error(
          "Student was saved, but the created StudentId was not returned by the API."
        );
      }
      // The master SP has already inserted StudentDetails. BookSend needs its own inserts.
      // Do not retry addStudent if this step fails: master is already created.
      try {
        await addStudentBookSendRows(createdStudentId, request.StudentDetails);
      } catch (bookSendError) {
        console.error("STUDENT_BOOK_SEND_INSERT_FAILED", {createdStudentId, bookSendError});
        throw new Error(`Student ${createdStudentId} saved, but book-send rows failed: ${bookSendError.message}. Do not resubmit registration; reconcile BookSend rows.`);
      }
      const savedRecord = {
        ...request,
        apiResponse:
          responseData,
        studentId:
          createdStudentId,
      };
      console.log(
        "SAVED STUDENT RECORD:",
        savedRecord
      );
      if (
        typeof onStudentMasterSaved ===
        "function"
      ) {
        await onStudentMasterSaved(
          savedRecord
        );
      }
      return savedRecord;
    } catch (error) {
      throw new Error(
        getApiErrorMessage(
          error,
          "Unable to save student master"
        )
      );
    }
  };
  /* =======================================================
     UPDATE STUDENT MASTER API
     - Used ONLY in Edit mode
     - Add Student flow above remains unchanged
  ======================================================= */
  const buildUpdateStudentRequest =
    () => {
      const studentId =
        initial?.id ??
        initial?.Id ??
        initial?.studentId ??
        initial?.StudentId ??
        savedStudentMaster?.id ??
        savedStudentMaster?.Id ??
        savedStudentMaster?.studentId ??
        savedStudentMaster?.StudentId;
      if (!studentId) {
        throw new Error(
          "StudentId not found for update."
        );
      }
      return {
        Id: Number(studentId),
        DocumentNo:
          initial?.DocumentNo ??
          initial?.documentNo ??
          initial?.roll ??
          savedStudentMaster?.DocumentNo ??
          savedStudentMaster?.documentNo ??
          savedStudentMaster?.roll ??
          "",
        FirstName:
          f.firstName.trim(),
        MiddleName:
          f.middleName?.trim() ||
          "",
        LastName:
          f.lastName.trim(),
        Gender:
          getGenderId(f.gender),
        DOB:
          f.dob,
        FatherName:
          f.parentName.trim(),
        FatherPhone:
          f.parentMobile.trim(),
        FatherEmail:
          f.parentEmail?.trim() ||
          "",
        Address:
          f.address?.trim() ||
          "",
        Pincode:
          String(f.pincode || ""),
        StateId: 1,
        DistrictId: Number(f.district),
        TalukaId: Number(f.taluka),
        SchoolId:
          Number(f.schoolId),
        StdId:
          Number(f.class),
        StdStatusId: 0,
        ExamTypeId: Number(f.medium),
        /*
          UpdateStudentModel.IsActive is INT,
          so send 1 instead of true.
        */
        IsActive:
          1,
        // Edit skips the courier step: preserve saved amount if available.
        CourierCharges: Number(
          initial?.courierCharges ?? initial?.CourierCharges ??
          savedStudentMaster?.courierCharges ?? savedStudentMaster?.CourierCharges ??
          feeSummary.courierCharge ?? 0
        ),

        StudentDetails: buildStudentDetailsRequest(Number(studentId)).map(detail => ({
          ...detail,
          // StudentDetails edit should not clear already-saved tracking fields.
          PostTrackNo: (getExistingStudentDetails().find(row =>
            Number(row.examId ?? row.ExamId) === Number(detail.ExamId)
          )?.postTrackNo ?? getExistingStudentDetails().find(row =>
            Number(row.examId ?? row.ExamId) === Number(detail.ExamId)
          )?.PostTrackNo ?? "0"),
          PostTrackDate: getExistingStudentDetails().find(row =>
            Number(row.examId ?? row.ExamId) === Number(detail.ExamId)
          )?.postTrackDate ?? getExistingStudentDetails().find(row =>
            Number(row.examId ?? row.ExamId) === Number(detail.ExamId)
          )?.PostTrackDate ?? null,
          IsBookSend: toBoolean(getExistingStudentDetails().find(row =>
            Number(row.examId ?? row.ExamId) === Number(detail.ExamId)
          )?.isBookSend ?? getExistingStudentDetails().find(row =>
            Number(row.examId ?? row.ExamId) === Number(detail.ExamId)
          )?.IsBookSend),
        })),
      };
    };
  const updateStudentMaster =
    async () => {
      const request =
        buildUpdateStudentRequest();
      console.log(
        "UPDATE STUDENT REQUEST:",
        request
      );
      try {
        const response =
          await apiService.put(
            updateStudentUrl,
            request
          );
        const responseData =
          response?.data;
        console.log(
          "UPDATE STATUS:",
          response?.status
        );
        console.log(
          "UPDATE STUDENT RESPONSE:",
          responseData
        );
        const output = responseData?.output ?? responseData?.Output;
        const outputStatus = output?.status ?? output?.Status ?? "";
        const outputMessage = output?.message ?? output?.Message ?? "";
        const statusText = String(outputStatus).toLowerCase();
        const messageText = String(outputMessage).toLowerCase();
        const isDuplicate =
          statusText.includes("duplicate") ||
          statusText.includes("already") ||
          (messageText.includes("already") && messageText.includes("exist"));
        if (isDuplicate) {
          throw new Error(outputMessage || "Student already exists.");
        }
        if (
          responseData?.isSuccess ===
          false ||
          responseData?.IsSuccess ===
          false
        ) {
          throw new Error(
            outputMessage ||
            responseData?.failureReason ||
            responseData?.FailureReason ||
            responseData?.message ||
            responseData?.Message ||
            "Unable to update student"
          );
        }
        const studentId =
          request.Id;
        return {
          ...request,
          id:
            studentId,
          studentId:
            studentId,
          apiResponse:
            responseData,
        };
      } catch (error) {
        throw new Error(
          getApiErrorMessage(
            error,
            "Unable to update student"
          )
        );
      }
    };
  /* =======================================================
     NAVIGATION
  ======================================================= */
  const goToStep = (
    index
  ) => {
    setErrors({});
    setStep(index);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  const handleNext =
    async (registrationConfirmed = false) => {
      const stepErrors =
        validateStep();
      if (
        Object.keys(
          stepErrors
        ).length > 0
      ) {
        setErrors(
          stepErrors
        );
        return;
      }
      setErrors({});
      /*
        ADD MODE:
        Books & Courier -> addStudent/updateStudent -> Review -> Payment.
        EDIT MODE:
        Review is the final step.
        Review -> updateStudent -> back to list.
      */
      // Books & Courier: confirmation is mandatory before writing the student.
      if (!isEdit && currentStep.id === "courier" && registrationConfirmed !== true) {
        setRegistrationConfirmOpen(true);
        return;
      }
      // ==========================================
      // ADD MODE -> SAVE BEFORE REVIEW
      // ==========================================
      if (
        !isEdit &&
        currentStep.id ===
        "courier"
      ) {
        try {
          setPaymentProcessing(
            true
          );
          const existingSavedId = Number(
            savedStudentMaster?.studentId ??
            savedStudentMaster?.StudentId ??
            savedStudentMaster?.id ??
            savedStudentMaster?.Id ??
            0
          );
          let saved;
          if (existingSavedId > 0) {
            saved =
              await updateStudentMaster();
          } else {
            saved =
              await saveStudentMaster();
          }
          const savedId = Number(
            saved?.studentId ??
            saved?.StudentId ??
            saved?.id ??
            saved?.Id ??
            existingSavedId
          );
          let fullStudent = null;
          if (savedId > 0) {
            fullStudent =
              await fetchStudentById(
                savedId
              );
          }
          const fetchedDetails =
            fullStudent?.studentDetails ??
            fullStudent?.StudentDetails ??
            fullStudent?.stdDetlst ??
            fullStudent?.StdDetlst ??
            saved?.studentDetails ??
            saved?.StudentDetails ??
            [];
          const hydratedStudent = {
            ...savedStudentMaster,
            ...saved,
            ...(fullStudent || {}),
            id: savedId ||
              fullStudent?.id ||
              fullStudent?.Id,
            Id: savedId ||
              fullStudent?.Id ||
              fullStudent?.id,
            studentId: savedId ||
              fullStudent?.studentId ||
              fullStudent?.StudentId,
            StudentId: savedId ||
              fullStudent?.StudentId ||
              fullStudent?.studentId,
            studentDetails:
              fetchedDetails,
            StudentDetails:
              fetchedDetails,
          };
          console.log(
            "STUDENT SAVED BEFORE REVIEW:",
            hydratedStudent
          );
          setSavedStudentMaster(
            hydratedStudent
          );
          showNotification(
            "success",
            existingSavedId > 0
              ? "Student updated successfully!"
              : saved?.apiResponse?.message ||
              saved?.apiResponse?.Message ||
              "Student added successfully!"
          );
          goToStep(
            step + 1
          );
        } catch (error) {
          console.error(
            "Save Student Before Review Error:",
            error
          );
          const errorMessage =
            error?.message ||
            "Unable to save student. Please try again.";
          setErrors({
            update:
              errorMessage,
          });
          showNotification(
            "error",
            errorMessage
          );
        } finally {
          setPaymentProcessing(
            false
          );
        }
        return;
      }
      // ==========================================
      // REVIEW
      // ==========================================
      if (
        currentStep.id ===
        "review"
      ) {
        // EDIT MODE -> UPDATE ONLY, NO PAYMENT STEP
        if (isEdit) {
          try {
            setPaymentProcessing(
              true
            );
            const updated =
              await updateStudentMaster();
            const updatedUiRecord = {
              ...initial,
              ...buildPayload(),
              // Preserve these fields in the parent/UI record during Edit.
              exams: getInitialExamIds(initial),
              selectedExams: initial?.selectedExams,
              materials: initial?.materials,
              deliveryMethod: initial?.deliveryMethod ?? initial?.delivery?.method,
              courierAddress: initial?.courierAddress ?? initial?.delivery?.courierAddress,
              delivery: initial?.delivery,
              id:
                updated.id,
              Id:
                updated.id,
              studentId:
                updated.studentId,
              StudentId:
                updated.studentId,
              FirstName:
                updated.FirstName,
              MiddleName:
                updated.MiddleName,
              LastName:
                updated.LastName,
              Gender:
                updated.Gender,
              DOB:
                updated.DOB,
              FatherName:
                updated.FatherName,
              FatherPhone:
                updated.FatherPhone,
              FatherEmail:
                updated.FatherEmail,
              Address:
                updated.Address,
              Pincode:
                updated.Pincode,
              SchoolId:
                updated.SchoolId,
              StdId:
                updated.StdId,
              StdStatusId: 0,
              ExamTypeId: updated.ExamTypeId,
              IsActive:
                updated.IsActive,
              studentDetails:
                updated.StudentDetails,
            };
            if (
              typeof onSave ===
              "function"
            ) {
              await onSave(
                updatedUiRecord
              );
            } else {
              onBack?.();
            }
            showNotification(
              "success",
              updated?.apiResponse?.message ||
              updated?.apiResponse?.Message ||
              "Student updated successfully!"
            );
          } catch (error) {
            console.error(
              "Update Student Error:",
              error
            );
            const errorMessage =
              error?.message ||
              "Unable to update student. Please try again.";
            setErrors({
              update:
                errorMessage,
            });
            showNotification(
              "error",
              errorMessage
            );
          } finally {
            setPaymentProcessing(
              false
            );
          }
          return;
        }
        // ADD MODE -> already saved before Review.
        // Review simply continues to Payment.
        if (!savedStudentMaster) {
          const errorMessage =
            "Student is not saved yet. Please go back to Books & Courier and continue again.";
          setErrors({
            update:
              errorMessage,
          });
          showNotification(
            "error",
            errorMessage
          );
          return;
        }
        goToStep(
          step + 1
        );
        return;
      }
      if (
        step <
        visibleSteps.length - 1
      ) {
        goToStep(
          step + 1
        );
      }
    };
  const handleBackStep = () => {
    // ADD MODE:
    // Student already saved and Review page reached.
    // Do not allow going back.
    if (
      !isEdit &&
      currentStep.id === "review" &&
      savedStudentMaster
    ) {
      return;
    }
    if (step > 0) {
      goToStep(
        step - 1
      );
    } else {
      onBack();
    }
  };
  /* =======================================================
     EXAM SELECT / UNSELECT
  ======================================================= */
  const toggleExam = (
    examId
  ) => {
    if (isEdit) return; // Exam choices are locked when updating.
    setF((prev) => {
      const current =
        prev.exams || [];
      const exists =
        current.some(
          (id) =>
            String(id) ===
            String(examId)
        );
      const updated = exists
        ? current.filter(
          (id) =>
            String(id) !==
            String(examId)
        )
        : [
          ...current,
          examId,
        ];
      return {
        ...prev,
        exams: updated,
        // New exam selection defaults to all study material.
        // Existing selections made by the student remain unchanged.
        materials: exists
          ? prev.materials
          : {
            ...prev.materials,
            [examId]: "book",
          },
      };
    });
    setErrors((prev) => {
      const updated = {
        ...prev,
      };
      delete updated.exams;
      return updated;
    });
  };
  /* =======================================================
     MATERIAL CHANGE
  ======================================================= */
  /* =======================================================
     COURIER ADDRESS
  ======================================================= */
  const getCourierAddress =
    () => {
      if (
        f.useStudentAddress
      ) {
        return {
          addressLine1:
            f.address || "",
          village: "",
          district: "",
          pincode:
            f.pincode || "",
        };
      }
      return {
        ...f.courierAddress,
      };
    };
  /* =======================================================
     BUILD PAYLOAD
  ======================================================= */
  const buildPayload =
    () => {
      return {
        ...f,
        /* Parent */
        parent: {
          name:
            f.parentName,
          mobile:
            f.parentMobile,
          email:
            f.parentEmail ||
            null,
        },
        parentName:
          f.parentName,
        parentMobile:
          f.parentMobile,
        parentEmail:
          f.parentEmail,
        /* Existing */
        name:
          `${f.firstName} ${f.middleName} ${f.lastName}`
            .replace(
              /\s+/g,
              " "
            )
            .trim(),
        class:
          classLabel,
        standardId:
          f.class,
        schoolId:
          f.schoolId,
        gender:
          f.gender,
        dob:
          f.dob,
        phone:
          f.phone,
        altPhone:
          f.altPhone,
        email:
          f.email,
        address:
          f.address,
        pincode:
          f.pincode,
        exams:
          f.exams,
        status:
          f.status,
        /* Student */
        student: {
          fullName:
            `${f.firstName} ${f.middleName} ${f.lastName}`
              .replace(
                /\s+/g,
                " "
              )
              .trim(),
          firstName:
            f.firstName,
          middleName:
            f.middleName ||
            null,
          lastName:
            f.lastName ||
            null,
          dateOfBirth:
            f.dob || null,
          gender:
            f.gender || null,
          mobile:
            f.phone || null,
          alternatePhone:
            f.altPhone ||
            null,
          currentStandard:
            classLabel,
          currentStandardId:
            f.class,
          primaryMedium:
            mediumLabel ||
            null,
          stdStatusId: 0,
          examTypeId: Number(f.medium),
          schoolId:
            f.schoolId,
          schoolName:
            selectedSchool?.name ||
            "",
          address: {
            addressLine1:
              f.address || "",
            pincode:
              f.pincode || "",
          },
        },
        /* Examination */
        examSelection: {
          standardId:
            f.class,
          standard:
            classLabel,
          stdStatusId: 0,
          examTypeId: Number(f.medium),
          medium:
            mediumLabel,
        },
        selectedExams:
          selectedExams.map(
            (exam) => ({
              examId:
                exam.id,
              examName:
                exam.name,
              examFee:
                exam.fee,
              materialType:
                "book",
            })
          ),
        /* Materials */
        materials:
          Object.fromEntries((f.exams || []).map((id) => [id, "book"])),
        /* Delivery */
        CourierCharges: Number(feeSummary.courierCharge || 0),
        delivery: {
          method:
            f.deliveryMethod,
          useStudentAddress:
            f.useStudentAddress,
          courierAddress:
            f.deliveryMethod ===
              "courier"
              ? getCourierAddress()
              : null,
          courierCharge:
            feeSummary.courierCharge,
        },
        /* Totals */
        totals: {
          examFeeTotal:
            feeSummary.examFeeTotal,
          materialTotal:
            feeSummary.materialTotal,
          courierCharge:
            feeSummary.courierCharge,
          finalAmount:
            feeSummary.finalAmount,
        },
        /* Payment */
        payment: {
          status:
            f.paymentStatus,
          method:
            f.paymentMethod,
          paymentId:
            f.paymentId,
          amount:
            feeSummary.finalAmount,
        },
        paymentStatus:
          f.paymentStatus,
        paymentMethod:
          f.paymentMethod,
        paymentId:
          f.paymentId,
        totalAmount:
          feeSummary.finalAmount,
      };
    };
  /* =======================================================
     PAYMENT
  ======================================================= */
  const getPendingStudentDetailIds = (student) => {
    const details = Array.isArray(student?.StudentDetails)
      ? student.StudentDetails
      : Array.isArray(student?.studentDetails)
        ? student.studentDetails
        : [];
    return details
      .filter((detail) => {
        const pendingFees =
          Number(detail.PendingFees ?? 0) ||
          Math.max(
            Number(detail.ExamTotalFees ?? 0) -
            Number(detail.FeesPaid ?? 0),
            0
          );
        return pendingFees > 0;
      })
      .map((detail) => Number(detail.Id))
      .filter((id) => id > 0);
  };
  const studentId = 28;
  const fetchStudentById =
    async (studentId) => {
      try {
        const response =
          await apiService.get(
            `Student/getStudentById/${studentId}`
          );
        const responseData =
          response?.data;
        if (
          responseData?.isSuccess ===
          false ||
          responseData?.IsSuccess ===
          false
        ) {
          throw new Error(
            responseData?.failureReason ||
            responseData?.FailureReason ||
            responseData?.message ||
            responseData?.Message ||
            "Unable to load saved student details."
          );
        }
        const output =
          responseData?.output ??
          responseData?.Output;
        console.log(
          "ADD STUDENT RESPONSE:",
          responseData
        );
        if (!output) {
          throw new Error(
            "Saved student details were not returned."
          );
        }
        return output;
      } catch (error) {
        throw new Error(
          getApiErrorMessage(
            error,
            "Unable to load saved student details."
          )
        );
      }
    };
  const RAZORPAY_KEY_ID = "rzp_live_TkB8Olj4FbJVxc";
  const handlePayment = async () => {
    try {
      setPaymentProcessing(true);
      // =====================================================
      // 1. GET THE SAVED STUDENT ID
      // =====================================================
      const currentStudentId = Number(
        savedStudentMaster?.studentId ??
        savedStudentMaster?.StudentId ??
        savedStudentMaster?.id ??
        savedStudentMaster?.Id
      );
      console.log(
        "CURRENT STUDENT OBJECT:",
        savedStudentMaster
      );
      console.log(
        "CURRENT STUDENT ID:",
        currentStudentId
      );
      if (!currentStudentId) {
        throw new Error(
          "Saved Student ID is missing. Please save the student first."
        );
      }
      // =====================================================
      // 2. FETCH STUDENT FROM DATABASE
      // =====================================================
      const fullStudent =
        await fetchStudentById(
          currentStudentId
        );
      console.log(
        "STUDENT FROM getStudentById:",
        fullStudent
      );
      // =====================================================
      // 3. GET STUDENT ID FROM DATABASE RESPONSE
      // =====================================================
      const fetchedStudentId = Number(
        fullStudent?.id ??
        fullStudent?.Id
      );
      console.log(
        "FETCHED STUDENT ID:",
        fetchedStudentId
      );
      if (!fetchedStudentId) {
        throw new Error(
          "Student ID was not returned by getStudentById."
        );
      }
      // =====================================================
      // 4. GET STUDENT DETAILS
      // =====================================================
      const studentDetails =
        fullStudent?.stdDetlst ??
        fullStudent?.StdDetlst ??
        [];
      console.log(
        "STUDENT DETAIL LIST:",
        studentDetails
      );
      if (!Array.isArray(studentDetails)) {
        throw new Error(
          "Student details were not returned."
        );
      }
      // =====================================================
      // 5. GET ONLY DETAILS WITH PENDING FEES
      // =====================================================
      const studentDetailIds =
        studentDetails
          .filter((detail) => {
            const pendingFees = Number(
              detail?.pendingFees ??
              detail?.PendingFees ??
              0
            );
            return pendingFees > 0;
          })
          .map((detail) =>
            Number(
              detail?.id ??
              detail?.Id
            )
          )
          .filter((id) => id > 0);
      console.log(
        "STUDENT DETAIL IDS:",
        studentDetailIds
      );
      if (
        studentDetailIds.length === 0
      ) {
        throw new Error(
          "No pending exam fees were found for this student."
        );
      }
      // =====================================================
      // 6. CREATE RAZORPAY ORDER
      // =====================================================
      const orderResponse =
        await apiService.post(
          "Payment/createRazorpayOrder",
          {
            studentId:
              fetchedStudentId,
            studentDetailIds:
              studentDetailIds,
          }
        );
      const orderRoot =
        orderResponse?.data;
      if (
        orderRoot?.isSuccess ===
        false ||
        orderRoot?.IsSuccess ===
        false
      ) {
        throw new Error(
          orderRoot?.failureReason ||
          orderRoot?.FailureReason ||
          orderRoot?.message ||
          orderRoot?.Message ||
          "Unable to create Razorpay order."
        );
      }
      const orderData =
        orderRoot?.output ??
        orderRoot?.Output ??
        orderRoot;
      console.log(
        "RAZORPAY ORDER RESPONSE:",
        orderData
      );
      // =====================================================
      // 7. GET AMOUNT
      // =====================================================
      const amount =
        Number(orderData?.amount);
      console.log("PAYMENT AMOUNT CHECK", {
        examFees: feeSummary.examFeeTotal,
        courierCharges: feeSummary.courierCharge,
        frontendTotal: feeSummary.finalAmount,
        backendOrderAmount: amount,
      });

      if (
        Math.abs(amount - feeSummary.finalAmount) > 0.01
      ) {
        throw new Error(
          `Payment amount mismatch. Expected ₹${feeSummary.finalAmount}, ` +
          `but backend returned ₹${amount}. Please contact support.`
        );
      }
      if (
        !amount ||
        amount <= 0
      ) {
        throw new Error(
          "Invalid payment amount received."
        );
      }
      // =====================================================
      // 8. CHECK RAZORPAY SCRIPT
      // =====================================================
      if (
        typeof window ===
        "undefined" ||
        !window.Razorpay
      ) {
        throw new Error(
          "Razorpay Checkout script is not loaded."
        );
      }
      // =====================================================
      // 9. CHECK RAZORPAY KEY
      // =====================================================
      if (!RAZORPAY_KEY_ID) {
        throw new Error(
          "Razorpay Key ID is missing."
        );
      }
      // =====================================================
      // 10. RAZORPAY OPTIONS
      // =====================================================
      const options = {
        key: RAZORPAY_KEY_ID,
        amount:
          Math.round(
            amount * 100
          ),
        currency:
          orderData?.currency ||
          "INR",
        name:
          "Gore Exam",
        description:
          "Combined Examination Fee",
        order_id:
          orderData?.orderId,
        handler:
          async function (
            paymentResponse
          ) {
            try {
              console.log(
                "RAZORPAY PAYMENT RESPONSE:",
                paymentResponse
              );
              // =================================================
              // 11. VERIFY PAYMENT
              // =================================================
              const verifyResponse =
                await apiService.post(
                  "Payment/verifyRazorpayPayment",
                  {
                    orderId:
                      paymentResponse
                        .razorpay_order_id,
                    razorpayPaymentId:
                      paymentResponse
                        .razorpay_payment_id,
                    razorpaySignature:
                      paymentResponse
                        .razorpay_signature,
                  }
                );
              const verifyData =
                verifyResponse?.data;
              console.log(
                "VERIFY RESPONSE:",
                verifyData
              );
              if (
                verifyData?.isSuccess ===
                false ||
                verifyData?.IsSuccess ===
                false
              ) {
                throw new Error(
                  verifyData?.message ??
                  verifyData?.Message ??
                  verifyData?.failureReason ??
                  verifyData?.FailureReason ??
                  "Payment verification failed."
                );
              }
              // =================================================
              // 12. SUCCESS
              // =================================================
              alert("Payment completed successfully.");
              const paymentSaveData = {
                ...savedStudentMaster,
                studentId: fetchedStudentId,
                studentDetailIds: studentDetailIds,
                paymentStatus: "Success",
                razorpayPaymentId:
                  paymentResponse.razorpay_payment_id,
              };
              console.log(
                "PAYMENT SUCCESS DATA:",
                paymentSaveData
              );
              if (typeof onSave === "function") {
                await onSave(paymentSaveData);
              }
              setPaymentProcessing(false);
              // Give the navigation/state update a moment to complete,
              // then force the browser to reload the current page.
              setTimeout(() => {
                window.location.reload();
              }, 300);
            } catch (error) {
              console.error(
                "Payment verification error:",
                error
              );
              setErrors({
                payment:
                  error?.message ||
                  "Payment verification failed.",
              });
              setPaymentProcessing(
                false
              );
            }
          },
        modal: {
          ondismiss:
            () => {
              setPaymentProcessing(
                false
              );
            },
        },
        theme: {
          color:
            "#3399cc",
        },
      };
      // =====================================================
      // 13. INITIALIZE RAZORPAY
      // =====================================================
      console.log(
        "INITIALIZING RAZORPAY:",
        {
          key:
            RAZORPAY_KEY_ID,
          amount:
            options.amount,
          currency:
            options.currency,
          orderId:
            options.order_id,
        }
      );
      const razorpay =
        new window.Razorpay(
          options
        );
      // =====================================================
      // 14. PAYMENT FAILED
      // =====================================================
      razorpay.on(
        "payment.failed",
        function (response) {
          console.error(
            "RAZORPAY PAYMENT FAILED:",
            response?.error
          );
          alert(
            response?.error
              ?.description ||
            "Payment failed. Please try again."
          );
          setPaymentProcessing(
            false
          );
        }
      );
      // =====================================================
      // 15. OPEN RAZORPAY
      // =====================================================
      razorpay.open();
    } catch (error) {
      console.error(
        "Payment Error:",
        error
      );
      setErrors({
        payment:
          error?.message ||
          "Unable to process payment. Please try again.",
      });
      setPaymentProcessing(
        false
      );
    }
  };
  /* =======================================================
     STEP 2 - ACADEMIC + EXAM
  ======================================================= */
  const renderAcademicStep =
    () => {
      return (
        <div className="fsec">
          <div className="fsec-t">
            <I
              n="exam"
              s={14}
              c="var(--p)"
            />
            Academic & Exam Selection / शैक्षणिक माहिती व परीक्षा निवड
          </div>
          <div className="fgrid">
            <SearchableSchoolSelect
              options={filteredSchools}
              value={f.schoolId}
              onChange={(id) => s("schoolId", id)}
              disabled={lookupLoading || !f.taluka || !hasSchoolLocationData}
              placeholder={lookupLoading ? "Loading Schools…" : !f.taluka ? "Select State, District & Taluka first…" : "Search / Select School…"}
              error={errors.schoolId}
            />
            {!lookupLoading && f.taluka && (
              <div className="fg fall" style={{ fontSize: 12, color: "#9a3412" }}>
                तुमची शाळा यादीत नसेल तर 99228 15978 या नंबरशी संपर्क साधा.
              </div>
            )}
            <Select
              label="Medium / माध्यम"
              name="medium"
              value={f.medium}
              onChange={(e) => {
                /*
                  Medium does not control available exams.
                  Keep already selected exams/materials unchanged.
                */
                setF(
                  (prev) => ({
                    ...prev,
                    medium:
                      e.target
                        .value,
                  })
                );
                setErrors(
                  (prev) => {
                    const updated =
                    {
                      ...prev,
                    };
                    delete updated.medium;
                    return updated;
                  }
                );
              }}
              errors={errors}
              required
              disabled={lookupLoading}
            >
              <option value="">
                {lookupLoading ? "Loading Mediums…" : "Select Medium…"}
              </option>
              {!lookupLoading && mediumOptions.length === 0 && (
                <option value="" disabled>No mediums found</option>
              )}
              {mediumOptions.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                </option>
              ))}
            </Select>
            <Select
              label="Class / इयत्ता"
              name="class"
              value={f.class}
              onChange={(e) => {
                setF(
                  (prev) => ({
                    ...prev,
                    class:
                      e.target
                        .value,
                    standard:
                      e.target
                        .value,
                    exams: [],
                    materials: {},
                  })
                );
                setErrors(
                  (prev) => {
                    const updated =
                    {
                      ...prev,
                    };
                    delete updated.class;
                    delete updated.exams;
                    return updated;
                  }
                );
              }}
              errors={errors}
              required
              disabled={lookupLoading}
            >
              <option value="">
                {lookupLoading ? "Loading Classes…" : "Select Class…"}
              </option>
              {!lookupLoading && standardOptions.length === 0 && (
                <option value="" disabled>No classes found</option>
              )}
              {standardOptions.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                </option>
              ))}
            </Select>
          </div>
          {lookupLoading && (
            <div style={{ marginTop: 10, fontSize: 10.5, color: "var(--t3)" }}>
              Loading Class, Medium, School and Exam data...
            </div>
          )}
          {lookupError && (
            <div
              style={{
                marginTop: 10,
                padding: 10,
                borderRadius: 7,
                background: "#fef2f2",
                color: "#dc2626",
                fontSize: 10.5,
              }}
            >
              {lookupError}
            </div>
          )}
          {/* =================================================
              EXAMS
          ================================================= */}
          {!f.class || !f.medium ? (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                border:
                  "1px solid #e5e7eb",
                borderRadius: 8,
                background:
                  "#f8fafc",
                color:
                  "var(--t3)",
                fontSize: 11,
                textAlign:
                  "center",
              }}
            >
              Select Class and Medium to view
              available examinations.
            </div>
          ) : (
            <div
              style={{
                marginTop: 18,
              }}
            >
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  marginBottom:
                    10,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize:
                        12,
                      fontWeight:
                        700,
                    }}
                  >
                    Available
                    Examinations
                  </div>
                  <div
                    style={{
                      fontSize:
                        10,
                      color:
                        "var(--t3)",
                      marginTop:
                        2,
                    }}
                  >
                    {isEdit
                      ? "परीक्षा निवड अपडेट करता येणार नाही."
                      : "Select the examinations you want to register for."}
                  </div>
                </div>
                {selectedExams.length >
                  0 && (
                    <div
                      style={{
                        fontSize:
                          11,
                        fontWeight:
                          700,
                        color:
                          "var(--p)",
                      }}
                    >
                      Selected:{" "}
                      {
                        selectedExams.length
                      }
                    </div>
                  )}
              </div>
              <style>{`@media (max-width: 760px) { .exam-selection-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; } } @media (max-width: 390px) { .exam-selection-grid { grid-template-columns: minmax(0, 1fr) !important; } }`}</style>
              <div style={{ margin: "0 0 14px", color: "#16375e", fontSize: 12, fontWeight: 650 }}>
                {f.exams?.length || 0} examination(s) selected out of {availableExams.length} available ({f.exams?.length || 0} परीक्षा निवडल्या)
              </div>
              {availableExams.length === 0 ? (
                <div style={{ padding: 15, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 9, fontSize: 13 }}>
                  No examinations available for {classLabel} / {mediumLabel}.
                </div>
              ) : (
                <div className="exam-selection-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
                  {availableExams.map((exam, index) => {
                    const id = getExamId(exam);
                    const name = getExamName(exam);
                    const checked = f.exams?.some((selectedId) => String(selectedId) === String(id));
                    const closed = examIsClosed(exam);
                    return (
                      <div key={id} style={{ minWidth: 0, minHeight: 205, border: checked ? "1.5px solid #193d69" : "1px solid #dce5f0", outline: checked ? "2px solid #dbe5f3" : "none", borderRadius: 12, padding: "10px 10px 10px", background: closed ? "#fbfcfe" : "white", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center", color: closed ? "#8293a9" : "#142b4a" }}>
                        <div style={{ width: 56, height: 56, display: "grid", placeItems: "center", overflow: "hidden" }}><ExamCardImage exam={exam} /></div>
                        <div style={{ fontWeight: 700, fontSize: 13, minHeight: 30, display: "flex", alignItems: "center", justifyContent: "center", overflowWrap: "anywhere" }}>{name}</div>
                        {closed ? <div style={{ background: "#fff3f1", color: "#e58a81", borderRadius: 7, padding: "4px 6px", fontSize: 10 }}>Registration closed<br />(नोंदणी बंद झाली आहे)</div> : null}
                        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 5, width: "100%", alignItems: "center" }}>
                          <button type="button" disabled={false} onClick={() => setViewExam(exam)} title={`View ${name}`} aria-label={`View details for ${name}`} style={{ width: 34, height: 27, border: "1px solid #dbe4f1", color: closed ? "#a0aec0" : "#6381a7", borderRadius: 8, background: "white", display: "grid", placeItems: "center", cursor: "pointer" }}><EyeIcon /></button>
                          <button type="button" disabled={isEdit || closed} onClick={() => toggleExam(id)} style={{ width: "100%", minHeight: 33, border: "1px solid #193d69", background: checked ? "#193d69" : "#fff", color: checked ? "#fff" : "#193d69", opacity: (isEdit || closed) ? .45 : 1, borderRadius: 7, padding: "5px 6px", fontWeight: 650, fontSize: 12, cursor: (isEdit || closed) ? "not-allowed" : "pointer" }}>
                            {checked ? "◉  Selected (निवडले)" : "◯  Select (निवडा)"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <FieldError
                name="exams"
                errors={errors}
              />
              {/* =================================================
                  TOTAL EXAM FEE
              ================================================= */}
              {selectedExams.length >
                0 && (
                  <div
                    style={{
                      marginTop: 12,
                      padding:
                        "11px 13px",
                      border:
                        "1px solid #e5e7eb",
                      borderRadius: 8,
                      background:
                        "#f8fafc",
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      alignItems:
                        "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize:
                          11,
                        color:
                          "var(--t2)",
                      }}
                    >
                      Total Examination
                      Fee
                    </div>
                    <div
                      style={{
                        fontSize:
                          14,
                        fontWeight:
                          700,
                        color:
                          "var(--p)",
                      }}
                    >
                      {formatINR(
                        examFeeTotal
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
          {viewExam && (
            <div role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setViewExam(null); }} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(21,39,66,.57)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
              <div role="dialog" aria-modal="true" aria-labelledby="exam-popup-heading" style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 440, padding: 24, boxShadow: "0 20px 55px rgba(0,0,0,.22)", color: "#142b4a" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <h3 id="exam-popup-heading" style={{ margin: 0, fontSize: 18, fontWeight: 750 }}>{getExamName(viewExam)}</h3>
                  <button type="button" onClick={() => setViewExam(null)} aria-label="Close exam details" style={{ border: 0, background: "transparent", color: "#64748b", cursor: "pointer", padding: 3, fontSize: 24 }}>×</button>
                </div>
                {[
                  ["Standards (इयत्ता)", examField(viewExam, ["standardNames", "StandardNames", "standards", "Standards", "stdName", "StdName", "standardName", "StandardName"], classLabel)],
                  ["Mediums (माध्यम)", examField(viewExam, ["mediumNames", "MediumNames", "mediums", "Mediums", "mediumName", "MediumName", "medium", "Medium"], mediumLabel)],
                  // ["▦ Last Date (अंतिम तारीख)", examDate(examField(viewExam, ["registrationEndDate", "RegistrationEndDate", "lastDate", "LastDate", "lastDateOfRegistration", "LastDateOfRegistration"], null))],
                  ["▦ Exam Date (परीक्षा तारीख)", examDate(examField(viewExam, ["examDate", "ExamDate", "examinationDate", "ExaminationDate"], null))],
                  ["₹ Fee Info (शुल्क माहिती)", examField(viewExam, ["feeInfo", "FeeInfo", "feeDescription", "FeeDescription"], formatINR(getExamFee(viewExam)))],
                ].map(([label, value]) => <div key={label} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, alignItems: "start", fontSize: 12.5, padding: "4px 0" }}><span style={{ color: "#64748b" }}>{label}</span><span style={{ textAlign: "right", color: "#1e293b", overflowWrap: "anywhere" }}>{Array.isArray(value) ? value.map(v => typeof v === "object" ? (v.name || v.Name || v.id || v.Id) : v).join(", ") : String(value ?? "—")}</span></div>)}
                <div style={{ borderRadius: 9, padding: "13px 14px", background: "#f6e5bc", display: "flex", justifyContent: "space-between", marginTop: 13, fontSize: 13 }}><span>Fee for {classLabel} / {mediumLabel}:</span><strong>{formatINR(getExamFee(viewExam))}</strong></div>
                <button type="button" onClick={() => setViewExam(null)} style={{ marginTop: 22, width: "100%", background: "white", border: "1px solid #193d69", color: "#193d69", borderRadius: 8, padding: "12px", cursor: "pointer", fontWeight: 700 }}>Close (बंद करा)</button>
              </div>
            </div>
          )}
        </div>
      );
    };
  /* =======================================================
     STEP 1 - PERSONAL
  ======================================================= */
  const renderPersonalStep =
    () => {
      return (
        <div className="fsec">
          <div className="fsec-t">
            <I
              n="person"
              s={14}
              c="var(--p)"
            />
            Student Personal Details / विद्यार्थ्याची वैयक्तिक माहिती
          </div>
          <div className="fgrid">
            <Input
              label="First Name / विद्यार्थ्याचे पहिले नाव"
              name="firstName"
              value={
                f.firstName
              }
              onChange={(e) =>
                s(
                  "firstName",
                  e.target
                    .value
                )
              }
              errors={errors}
              required
              placeholder="Enter first name"
            />
            <Input
              label="Middle Name / विद्यार्थ्याचे मधले नाव"
              name="middleName"
              value={
                f.middleName
              }
              onChange={(e) =>
                s(
                  "middleName",
                  e.target
                    .value
                )
              }
              errors={errors}
              placeholder="Enter middle name"
            />
            <Input
              label="Last Name / विद्यार्थ्याचे आडनाव"
              name="lastName"
              value={
                f.lastName
              }
              onChange={(e) =>
                s(
                  "lastName",
                  e.target
                    .value
                )
              }
              errors={errors}
              required
              placeholder="Enter last name"
            />
            <Select
              label="Gender / लिंग"
              name="gender"
              value={
                f.gender
              }
              onChange={(e) =>
                s(
                  "gender",
                  e.target
                    .value
                )
              }
              errors={errors}
              required
            >
              <option value="">
                Select…
              </option>
              <option value="Male">
                Male
              </option>
              <option value="Female">
                Female
              </option>
              <option value="Other">
                Other
              </option>
            </Select>
            <Input
              label="Date of Birth / जन्मतारीख"
              name="dob"
              type="date"
              value={f.dob}
              max={getTodayDate()}
              onChange={(e) => {
                const value =
                  e.target.value;
                if (
                  value &&
                  value > getTodayDate()
                ) {
                  showNotification(
                    "error",
                    "Future date is not allowed."
                  );
                  return;
                }
                s(
                  "dob",
                  value
                );
              }}
              errors={errors}
              required
            />
            <Input
              label="Parent Mobile / पालकांचा मोबाईल नंबर"
              name="parentMobile"
              value={
                f.parentMobile
              }
              onChange={(e) => {
                const value =
                  e.target.value.replace(
                    /\D/g,
                    ""
                  );
                if (
                  value.length <=
                  10
                ) {
                  s(
                    "parentMobile",
                    value
                  );
                }
              }}
              errors={errors}
              required
              maxLength={10}
              inputMode="numeric"
              placeholder="10 digit mobile number"
            />
            <Input
              label="Parent Email / पालकांचा ईमेल"
              name="parentEmail"
              type="email"
              value={
                f.parentEmail
              }
              onChange={(e) =>
                s(
                  "parentEmail",
                  e.target
                    .value
                )
              }
              errors={errors}
              required
              placeholder="parent@example.com"
            />
            <Input
              label="Pincode / पिनकोड"
              name="pincode"
              value={
                f.pincode
              }
              onChange={(e) => {
                const value =
                  e.target.value.replace(
                    /\D/g,
                    ""
                  );
                if (
                  value.length <=
                  6
                ) {
                  s(
                    "pincode",
                    value
                  );
                }
              }}
              errors={errors}
              maxLength={6}
              inputMode="numeric"
              placeholder="6 digit pincode"
            />
            <Select label="State (राज्य) / राज्य" name="state" value="Maharashtra" onChange={() => { }} errors={errors} required disabled>
              <option value="Maharashtra">Maharashtra</option>
            </Select>
            <Select label="District (जिल्हा) / जिल्हा" name="district" value={f.district}
              onChange={(e) => { if (e.target.value === "__add__") { setAddDistrictOpen(true); return; } setF(prev => ({ ...prev, district: e.target.value, taluka: "", schoolId: "" })); setErrors(prev => ({ ...prev, district: "", taluka: "", schoolId: "" })); }}
              errors={errors} required disabled={locationsLoading}>
              <option value="">Select District…</option>
              {districtOptions.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              <option value="__add__">＋ Add New District / नवीन जिल्हा जोडा</option>
            </Select>
            <Select label="Taluka (तालुका) / तालुका" name="taluka" value={f.taluka}
              onChange={(e) => { if (e.target.value === "__add__") { setAddTalukaOpen(true); return; } setF(prev => ({ ...prev, taluka: e.target.value, schoolId: "" })); setErrors(prev => ({ ...prev, taluka: "", schoolId: "" })); }}
              errors={errors} required disabled={!f.district}>
              <option value="">Select Taluka…</option>
              {talukaOptions.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              <option value="__add__">＋ Add New Taluka / नवीन तालुका जोडा</option>
            </Select>
            {locationError && <div className="fg fall" style={{ color: "#a16207", fontSize: 12 }}>District/Taluka Master API: {locationError}</div>}
            {addDistrictOpen && <div className="modal-bg" style={{ zIndex: 10000 }} onClick={() => setAddDistrictOpen(false)}>
              <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
                <div className="mhdr"><div className="mhdr-t">Add New District</div><button type="button" className="xbtn" onClick={() => setAddDistrictOpen(false)}>×</button></div>
                <div className="mbody"><label className="fl">District Name / जिल्ह्याचे नाव</label><input className="fi" value={newDistrict} onChange={e => setNewDistrict(e.target.value)} placeholder="Enter district name" /></div>
                <div className="mfoot"><button type="button" className="btn btn-g" onClick={() => setAddDistrictOpen(false)}>Cancel / रद्द करा</button><button type="button" className="btn btn-p" disabled={districtSaving} onClick={addDistrict}>{districtSaving ? "Adding... / जोडत आहे..." : "Add / जोडा"}</button></div>
              </div>
            </div>}
            {addTalukaOpen && <div className="modal-bg" style={{ zIndex: 10000 }} onClick={() => setAddTalukaOpen(false)}>
              <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
                <div className="mhdr"><div className="mhdr-t">Add New Taluka</div><button type="button" className="xbtn" onClick={() => setAddTalukaOpen(false)}>×</button></div>
                <div className="mbody"><label className="fl">District / जिल्हा</label><input className="fi" readOnly value={districtList.find(x => String(x.id) === String(f.district))?.name || ""} /><label className="fl" style={{ marginTop: 10 }}>Taluka Name</label><input className="fi" value={newTaluka} onChange={e => setNewTaluka(e.target.value)} placeholder="Enter taluka name" /></div>
                <div className="mfoot"><button type="button" className="btn btn-g" onClick={() => setAddTalukaOpen(false)}>Cancel / रद्द करा</button><button type="button" className="btn btn-p" disabled={talukaSaving} onClick={addTaluka}>{talukaSaving ? "Adding... / जोडत आहे..." : "Add / जोडा"}</button></div>
              </div>
            </div>}
            <div className="fg fall">
              <label className="fl">
                Address / पत्ता
              </label>
              <input
                className="fi"
                name="address"
                value={
                  f.address
                }
                onChange={(e) =>
                  s(
                    "address",
                    e.target
                      .value
                  )
                }
                placeholder="Street, Area, City"
              />
              <FieldError
                name="address"
                errors={errors}
              />
            </div>
          </div>
        </div>
      );
    };
  /* =======================================================
     STEP 4 - REVIEW
  ======================================================= */
  const renderReviewStep =
    () => {
      const school = selectedSchool;
      const studentFullName =
        `${f.firstName} ${f.middleName} ${f.lastName}`
          .replace(
            /\s+/g,
            " "
          )
          .trim();
      const credentialsSource =
        savedStudentMaster ||
        initial ||
        {};
      const credentialsOutput =
        credentialsSource?.apiResponse?.output ??
        credentialsSource?.apiResponse?.Output ??
        {};
      const reviewUsername =
        credentialsSource?.userName ||
        credentialsOutput?.userName ||
        credentialsOutput?.UserName ||
        credentialsSource?.UserName ||
        "—";
      const reviewPassword =
        credentialsSource?.password ??
        credentialsSource?.Password ??
        credentialsSource?.plainPassword ??
        credentialsSource?.PlainPassword ??
        credentialsSource?.generatedPassword ??
        credentialsSource?.GeneratedPassword ??
        credentialsOutput?.password ??
        credentialsOutput?.Password ??
        credentialsOutput?.plainPassword ??
        credentialsOutput?.PlainPassword ??
        credentialsOutput?.generatedPassword ??
        credentialsOutput?.GeneratedPassword ??
        "";
      return (
        <div>
          {/* Login Credentials */}
          <ReviewSection
            title="Login Credentials"
          >
            <ReviewRow
              label="Username / वापरकर्तानाव"
              value={
                reviewUsername ||
                "—"
              }
            />
            <ReviewRow
              label="Password / पासवर्ड"
              value={
                reviewPassword ||
                "—"
              }
            />
          </ReviewSection>
          {/* Academic */}
          <ReviewSection
            title="Academic & Examinations"
            onEdit={
              isEdit
                ? () => goToStep(1)
                : undefined
            }
          >
            <ReviewRow
              label="Class / इयत्ता"
              value={
                classLabel
              }
            />
            <ReviewRow
              label="Medium / माध्यम"
              value={
                mediumLabel
              }
            />
            <ReviewRow
              label="School / शाळा"
              value={
                school?.name ||
                "—"
              }
            />
            <div
              style={{
                gridColumn:
                  "1 / -1",
              }}
            >
              <div
                style={{
                  fontSize:
                    9.5,
                  color:
                    "var(--t3)",
                  marginBottom:
                    6,
                }}
              >
                Selected
                Examinations
              </div>
              <div
                style={{
                  display:
                    "flex",
                  flexDirection:
                    "column",
                  gap: 6,
                }}
              >
                {selectedExams.length ===
                  0 ? (
                  <span
                    style={{
                      color:
                        "var(--t3)",
                      fontSize:
                        11,
                    }}
                  >
                    No exams
                    selected
                  </span>
                ) : (
                  selectedExams.map(
                    (exam) => (
                      <div
                        key={
                          exam.id
                        }
                        style={{
                          display:
                            "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "center",
                          padding:
                            "8px 10px",
                          background:
                            "#f8fafc",
                          borderRadius:
                            6,
                        }}
                      >
                        <span
                          style={{
                            fontSize:
                              11,
                            fontWeight:
                              600,
                          }}
                        >
                          {
                            exam.name
                          }
                        </span>
                        <span
                          style={{
                            fontSize:
                              11,
                            fontWeight:
                              700,
                            color:
                              "var(--p)",
                          }}
                        >
                          {formatINR(
                            exam.fee
                          )}
                        </span>
                      </div>
                    )
                  )
                )}
              </div>
            </div>
          </ReviewSection>
          {/* Personal */}
          <ReviewSection
            title="Student Personal Details"
            onEdit={
              isEdit
                ? () => goToStep(0)
                : undefined
            }
          >
            <ReviewRow
              label="Name / नाव"
              value={
                studentFullName
              }
            />
            <ReviewRow
              label="Gender / लिंग"
              value={
                f.gender
              }
            />
            <ReviewRow
              label="Date of Birth / जन्मतारीख"
              value={
                f.dob
              }
            />
            <ReviewRow
              label="Parent Mobile / पालकांचा मोबाईल नंबर"
              value={
                f.parentMobile ||
                "—"
              }
            />
            <ReviewRow
              label="Parent Email / पालकांचा ईमेल"
              value={
                f.parentEmail ||
                "—"
              }
            />
            <ReviewRow
              label="Address / पत्ता"
              value={
                f.address
              }
            />
            <ReviewRow
              label="Pincode / पिनकोड"
              value={
                f.pincode
              }
            />
          </ReviewSection>
          {/* Books & Courier */}
          <ReviewSection
            title="Books & Courier"
            onEdit={
              isEdit
                ? () => goToStep(2)
                : undefined
            }
          >
            <ReviewRow
              label="Delivery Method / वितरण पद्धत"
              value={
                DELIVERY_OPTIONS.find(
                  (x) =>
                    x.value ===
                    f.deliveryMethod
                )?.label ||
                f.deliveryMethod ||
                "—"
              }
            />
            <ReviewRow
              label="Use Student Address / विद्यार्थ्याचा पत्ता वापरा"
              value={
                f.deliveryMethod === "courier"
                  ? f.useStudentAddress
                    ? "Yes"
                    : "No"
                  : "—"
              }
            />
            <div
              style={{
                gridColumn:
                  "1 / -1",
              }}
            >
              <div
                style={{
                  fontSize: 9.5,
                  color: "var(--t3)",
                  marginBottom: 6,
                }}
              >
                Study Material / अभ्यास साहित्य
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {selectedExams.map(exam => {
                  const names = (isEdit
                    ? booksForExam(exam).filter(book =>
                      (f.selectedBookIds?.[String(exam.id)] || []).map(String).includes(String(book.bookDetailId)))
                    : autoBooksForExam(exam)).map(book => book.bookName);
                  return (
                    <div key={String(exam.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "#f8fafc", borderRadius: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>{exam.name}</span>
                      <span style={{ fontSize: 10.5, color: "var(--t2)" }}>{names.join(", ") || "No books selected"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </ReviewSection>
          {/* Fee */}
          <FeeSummary
            summary={
              feeSummary
            }
          />
        </div>
      );
    };
  /* =======================================================
     STEP 4 - COURIER
  ======================================================= */
  const renderCourierStep =
    () => {
      return (
        <div className="fsec">
          <div className="fsec-t">
            <I
              n="box"
              s={14}
              c="var(--p)"
            />
            Books & Courier / पुस्तके व कुरिअर
          </div>
          {selectedExams.length ===
            0 ? (
            <div
              style={{
                padding: 14,
                background:
                  "#fff7ed",
                border:
                  "1px solid #fed7aa",
                borderRadius: 8,
                fontSize: 11,
                color:
                  "#9a3412",
              }}
            >
              Please go back
              and select at
              least one
              examination
              first.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 12 }}>
                निवडलेल्या प्रत्येक परीक्षेची पहिली दोन पुस्तके आपोआप निवडली जातील.
              </div>
              {bookLoading && <div style={{ fontSize: 11 }}>Loading books...</div>}
              {bookError && <div role="alert" style={{ fontSize: 11, color: "#b91c1c" }}>{bookError}</div>}
              {!bookLoading && !bookError && selectedExams.map(exam => {
                const books = booksForExam(exam);
                const selected = autoBooksForExam(exam).map(book => String(book.bookDetailId));
                return (
                  <div key={String(exam.id)} style={{ padding: "10px 12px", marginBottom: 8, border: "1px solid #e5e7eb", borderRadius: 8 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 7 }}>{exam.name}</div>
                    {books.length === 0 ? (
                      <div style={{ fontSize: 11, color: "#92400e" }}>No books mapped to this exam in Book Master.</div>
                    ) : books.map(book => (
                      <label key={`${exam.id}-${book.bookDetailId}`} style={{ display: "flex", alignItems: "center", gap: 9, padding: "5px 2px", fontSize: 11.5, color: "var(--t2)" }}>
                        <input type="checkbox"
                          checked={selected.includes(String(book.bookDetailId))}
                          disabled
                          readOnly
                          aria-label={`${book.bookName} ${selected.includes(String(book.bookDetailId)) ? "automatically selected" : "not selected"}`}
                          style={{ width: 16, height: 16, accentColor: "#234C80" }} />
                        <span>{book.bookName}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
              <p style={{ marginTop: 12, fontSize: 11, lineHeight: 1.7, color: "#7c3d12", background: "#fff7ed", padding: "10px 12px", borderRadius: 8 }}>
                सूचना: नोंदणी केल्यानंतर पुस्तके दिली जातील. प्रश्नसंच परीक्षेच्या दोन महिने आधी दिले जातील. प्रश्नसंच पाठविण्याचे कुरिअर शुल्क स्वतंत्रपणे आकारले जाईल.
              </p>
              {/* Delivery */}
              <div
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop:
                    "1px solid #eef2f7",
                }}
              >
                <div
                  style={{
                    fontWeight:
                      700,
                    fontSize:
                      12,
                    marginBottom:
                      10,
                  }}
                >
                  Delivery
                  Method
                </div>
                <div
                  style={{
                    display:
                      "flex",
                    gap: 10,
                    flexWrap:
                      "wrap",
                  }}
                >
                  {DELIVERY_OPTIONS.map(
                    (option) => {
                      const active =
                        f.deliveryMethod ===
                        option.value;
                      return (
                        <button
                          type="button"
                          key={
                            option.value
                          }
                          onClick={() =>
                            s(
                              "deliveryMethod",
                              option.value
                            )
                          }
                          style={{
                            padding:
                              "9px 12px",
                            borderRadius:
                              7,
                            border:
                              active
                                ? "1px solid var(--p)"
                                : "1px solid #dbe1e8",
                            background:
                              active
                                ? "#eef4ff"
                                : "#fff",
                            color:
                              active
                                ? "var(--p)"
                                : "var(--t2)",
                            cursor:
                              "pointer",
                            fontSize:
                              10.5,
                            fontWeight:
                              600,
                          }}
                        >
                          {
                            option.label
                          }
                          <span
                            style={{
                              display:
                                "block",
                              fontSize:
                                9,
                              marginTop:
                                2,
                              fontWeight:
                                400,
                            }}
                          >
                            {
                              option.mr
                            }
                          </span>
                        </button>
                      );
                    }
                  )}
                </div>
              </div>
              {/* Courier Address */}
              {f.deliveryMethod === "courier" && (
                <p style={{ marginTop: 12, fontSize: 12, fontWeight: 600, lineHeight: 1.7, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 12px", color: "#1e3a8a" }}>
                  पुस्तकांचे कुरिअर शुल्क: ₹50 प्रति परीक्षा × {f.exams?.length || 0} = {formatINR(feeSummary.courierCharge)}.
                  प्रश्नसंचाचे कुरिअर शुल्क स्वतंत्रपणे आकारले जाईल.
                </p>
              )}
              {f.deliveryMethod ===
                "courier" && (
                  <div
                    style={{
                      marginTop: 18,
                      padding: 14,
                      background:
                        "#f8fafc",
                      border:
                        "1px solid #e5e7eb",
                      borderRadius: 8,
                    }}
                  >
                    <label
                      style={{
                        display:
                          "flex",
                        alignItems:
                          "center",
                        gap: 7,
                        fontSize:
                          11,
                        fontWeight:
                          600,
                        marginBottom:
                          12,
                        cursor:
                          "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          f.useStudentAddress
                        }
                        onChange={(
                          e
                        ) =>
                          s(
                            "useStudentAddress",
                            e.target
                              .checked
                          )
                        }
                      />
                      Use student's
                      address for
                      courier
                    </label>
                    {f.useStudentAddress ? (
                      <div
                        style={{
                          padding:
                            10,
                          background:
                            "#fff",
                          border:
                            "1px solid #e5e7eb",
                          borderRadius:
                            7,
                          fontSize:
                            10.5,
                          color:
                            "var(--t2)",
                        }}
                      >
                        {f.address ||
                          "Student address not entered"}
                        {f.pincode && (
                          <>
                            {" "}
                            -{" "}
                            {
                              f.pincode
                            }
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="fgrid">
                        <div className="fg fall">
                          <label className="fl">
                            Address *
                          </label>
                          <input
                            className="fi"
                            value={
                              f
                                .courierAddress
                                .addressLine1
                            }
                            onChange={(
                              e
                            ) =>
                              setCourier(
                                "addressLine1",
                                e.target
                                  .value
                              )
                            }
                          />
                          <FieldError
                            name="courier.addressLine1"
                            errors={
                              errors
                            }
                          />
                        </div>
                        <div className="fg">
                          <label className="fl">
                            Village *
                          </label>
                          <input
                            className="fi"
                            value={
                              f
                                .courierAddress
                                .village
                            }
                            onChange={(
                              e
                            ) =>
                              setCourier(
                                "village",
                                e.target
                                  .value
                              )
                            }
                          />
                          <FieldError
                            name="courier.village"
                            errors={
                              errors
                            }
                          />
                        </div>
                        <div className="fg">
                          <label className="fl">
                            District *
                          </label>
                          <input
                            className="fi"
                            value={
                              f
                                .courierAddress
                                .district
                            }
                            onChange={(
                              e
                            ) =>
                              setCourier(
                                "district",
                                e.target
                                  .value
                              )
                            }
                          />
                          <FieldError
                            name="courier.district"
                            errors={
                              errors
                            }
                          />
                        </div>
                        <div className="fg">
                          <label className="fl">
                            Pincode *
                          </label>
                          <input
                            className="fi"
                            maxLength={
                              6
                            }
                            inputMode="numeric"
                            value={
                              f
                                .courierAddress
                                .pincode
                            }
                            onChange={(
                              e
                            ) => {
                              const value =
                                e.target.value.replace(
                                  /\D/g,
                                  ""
                                );
                              if (
                                value.length <=
                                6
                              ) {
                                setCourier(
                                  "pincode",
                                  value
                                );
                              }
                            }}
                          />
                          <FieldError
                            name="courier.pincode"
                            errors={
                              errors
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </>
          )}
        </div>
      );
    };
  /* =======================================================
     STEP 5 - PAYMENT
  ======================================================= */
  const renderPaymentStep =
    () => {
      return (
        <div>
          <div className="fsec">
            <div className="fsec-t">
              <I
                n="check"
                s={14}
                c="var(--p)"
              />
              Payment / पेमेंट
            </div>
            <div
              style={{
                display:
                  "flex",
                flexDirection:
                  "column",
                gap: 10,
              }}
            >
              <PaymentRow
                label="Examination Fees / परीक्षा शुल्क"
                amount={
                  feeSummary.examFeeTotal
                }
              />
              <PaymentRow
                label="Study Material / अभ्यास साहित्य"
                amount={
                  feeSummary.materialTotal
                }
              />
              <PaymentRow
                label="पुस्तकांचे कुरिअर शुल्क (₹50 प्रति परीक्षा)"
                amount={
                  feeSummary.courierCharge
                }
              />
              <div
                style={{
                  borderTop:
                    "1px solid #e5e7eb",
                  paddingTop:
                    13,
                  marginTop: 5,
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  fontSize: 15,
                  fontWeight:
                    700,
                }}
              >
                <span>
                  Total Amount
                </span>
                <span>
                  {formatINR(
                    feeSummary.finalAmount
                  )}
                </span>
              </div>
            </div>
          </div>
          {errors.payment && (
            <div
              style={{
                marginTop: 10,
                padding: 10,
                borderRadius: 7,
                background:
                  "#fef2f2",
                color:
                  "#dc2626",
                fontSize: 11,
              }}
            >
              {
                errors.payment
              }
            </div>
          )}
          <div
            style={{
              marginTop: 14,
              padding: 12,
              background:
                "#f8fafc",
              border:
                "1px solid #e5e7eb",
              borderRadius: 8,
              fontSize: 10.5,
              color:
                "var(--t3)",
            }}
          >
            <strong
              style={{
                color:
                  "var(--t2)",
              }}
            >
              Payment / पेमेंट
              Information
            </strong>
            <div
              style={{
                marginTop: 4,
              }}
            >
              {typeof onStartPayment ===
                "function"
                ? "Click the payment button below to continue."
                : "Student Master is saved. Razorpay API can be connected here later."}
            </div>
          </div>
        </div>
      );
    };
  /* =======================================================
     CURRENT STEP
  ======================================================= */
  const renderCurrentStep =
    () => {
      switch (
      currentStep.id
      ) {
        case "academic":
          return renderAcademicStep();
        case "personal":
          return renderPersonalStep();
        case "courier":
          return renderCourierStep();
        case "review":
          return renderReviewStep();
        case "payment":
          return renderPaymentStep();
        default:
          return null;
      }
    };
  /* =======================================================
     RETURN
  ======================================================= */
  return (
    <>
      {publicRegistration && <style>{`
        .student-public-registration { box-sizing:border-box; width:100%; max-width:950px; margin:0 auto !important; background:#fff; color:#19253d; border-radius:22px; overflow:hidden; box-shadow:0 22px 65px rgba(32,61,120,.14); border:1px solid #dce6fb; }
        .student-public-registration, .student-public-registration * { box-sizing:border-box; }
        .student-public-registration .spr-header { background:linear-gradient(115deg,#29377f 0%,#3e62ba 56%,#0e8fbb 100%); color:white; padding:28px 32px; }
        .student-public-registration .spr-header-inner { display:flex; justify-content:space-between; align-items:flex-start; gap:18px; }
        .student-public-registration .spr-heading { min-width:0; flex:1 1 auto; }
        .student-public-registration .spr-brand { display:flex; align-items:center; gap:16px; min-width:0; }
        .student-public-registration .spr-logo-box { flex:0 0 auto; display:flex; align-items:center; justify-content:center; width:76px; height:76px; padding:7px; border-radius:17px; background:#fff; box-shadow:0 8px 24px rgba(13,31,91,.2); }
        .student-public-registration .spr-logo { width:100%; height:100%; object-fit:contain; border-radius:9px; }
        .student-public-registration .spr-brand-copy { min-width:0; }
        .student-public-registration .spr-title { color:#fff; font-size:29px; font-weight:800; margin:6px 0 0; line-height:1.25; }
        .student-public-registration .spr-subtitle { color:#eaf3ff; font-size:14px; line-height:1.55; margin-top:9px; }
        .student-public-registration .spr-back { flex:0 0 auto; display:inline-flex; justify-content:center; align-items:center; gap:8px; padding:11px 16px; background:white; color:#29377f; border:1px solid rgba(255,255,255,.75); font-size:13px; font-weight:750; border-radius:11px; cursor:pointer; min-height:43px; box-shadow:0 4px 14px rgba(0,0,0,.1); }
        .student-public-registration .spr-back:hover { background:#f1f5ff; }
        .student-public-registration .spr-back:focus-visible { outline:3px solid #facc15; outline-offset:3px; }
        .student-public-registration .spr-body { padding:28px 32px 32px; }
        .student-public-registration .spr-steps { scrollbar-width:thin; }
        .student-public-registration .fsec { border:1px solid #e0e9f9; background:#fbfdff; border-radius:14px; padding:18px; margin-bottom:18px; }
        .student-public-registration .fsec-t { color:#253f87; font-size:14px; font-weight:800; margin-bottom:14px; }
        .student-public-registration .fgrid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:15px 18px; }
        .student-public-registration .fgrid > .fall { grid-column:1 / -1; }
        .student-public-registration .fg { min-width:0; }
        .student-public-registration .fl { display:block; color:#344466; font-size:12px; font-weight:700; margin-bottom:7px; }
        .student-public-registration .fi { width:100%; max-width:100%; min-width:0; border:1px solid #cddaf0; border-radius:9px; background:white; min-height:41px; padding:9px 11px; font-size:13px; color:#1e293b; }
        .student-public-registration .fi:focus { outline:0; border-color:#4766c8; box-shadow:0 0 0 3px rgba(71,102,200,.14); }
        .student-public-registration .btn { border-radius:9px; min-height:40px; padding:9px 16px; font-weight:700; }
        .student-public-registration .btn-p { background:#3658b7; color:#fff; }
        .student-public-registration .btn-g { background:#f2f5fc; color:#31436a; border:1px solid #d4dff0; }
        @media(max-width:767px) {
          .student-public-registration { border-radius:15px; }
          .student-public-registration .spr-header { padding:21px 18px; }
          .student-public-registration .spr-header-inner { flex-direction:column; align-items:stretch; gap:17px; }
          .student-public-registration .spr-title { font-size:24px; }
          .student-public-registration .spr-brand { gap:12px; }
          .student-public-registration .spr-logo-box { width:62px; height:62px; border-radius:13px; padding:6px; }
          .student-public-registration .spr-subtitle { font-size:12px; }
          .student-public-registration .spr-back { align-self:flex-start; }
          .student-public-registration .spr-body { padding:18px 14px 24px; }
          .student-public-registration .fsec { padding:15px 12px; }
          .student-public-registration .fgrid { grid-template-columns:minmax(0,1fr) !important; gap:13px; }
          .student-public-registration .fgrid > * { grid-column:1 / -1 !important; }
          .student-public-registration .spr-steps { overflow-x:auto; padding:13px 6px 12px !important; }
          .student-public-registration .spr-steps > button { min-width:58px !important; }
          .student-public-registration .spr-steps > button span { font-size:9px !important; }
        }
        @media(max-width:380px) {
          .student-public-registration .spr-header { padding:17px 14px; }
          .student-public-registration .spr-body { padding:14px 10px 20px; }
          .student-public-registration .spr-title { font-size:21px; }
          .student-public-registration .spr-logo-box { width:52px; height:52px; padding:5px; }
          .student-public-registration .spr-brand { gap:10px; }
          .student-public-registration .spr-steps > button { min-width:54px !important; }
        }
      `}</style>}
      {!publicRegistration && (
        <BC
          items={["Students", title]}
          onBack={onBack}
        />
      )}
      <div
        className={publicRegistration ? "card cp student-public-registration" : "card cp"}
        style={{
          maxWidth: publicRegistration ? 950 : 820,
          width: "100%",
          ...(publicRegistration && {
            marginLeft: "auto",
            marginRight: "auto",
            padding: 0,
            boxShadow: "0 24px 70px rgba(42, 58, 111, 0.15)",
          }),
        }}
      >
        {/* PUBLIC REGISTRATION HEADER: back button stays inside the form */}
        {publicRegistration ? (
          <div className="spr-header relative overflow-hidden">
            <div className="pointer-events-none absolute -right-10 -top-16 h-48 w-48 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-20 right-24 h-40 w-40 rounded-full bg-white/10" />
            <div className="spr-header-inner relative">
              <div className="spr-heading">
                <div className="spr-brand">
                  <div className="spr-logo-box">
                    <img className="spr-logo" src={goreLogo} alt="Gore Exam logo" />
                  </div>
                  <div className="spr-brand-copy">
                    <div className="mb-2 inline-flex items-center rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs font-semibold tracking-wide text-white">
                      GORE EXAM • ONLINE REGISTRATION
                    </div>
                    <h1 className="spr-title">Student Registration</h1>
                    <p className="spr-subtitle">
                      विद्यार्थी नोंदणी • Complete your details step by step
                    </p>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="spr-back"
              >
                <span aria-hidden="true">←</span> Back to Login
              </button>
            </div>
          </div>
        ) : (
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18 }}>
            {title}
          </div>
        )}
        <div className={publicRegistration ? "spr-body" : ""}>
          {/* STEP INDICATOR */}
          <div
            className={publicRegistration ? "spr-steps" : ""}
            style={{
              display:
                "flex",
              alignItems:
                "flex-start",
              gap: 0,
              marginBottom: 24,
              overflowX:
                "auto",
              paddingBottom: 4,
              ...(publicRegistration ? {
                background: "#f0f5ff",
                border: "1px solid #dbeafe",
                borderRadius: 16,
                padding: "16px 10px 12px",
              } : {}),
            }}
          >
            {visibleSteps.map(
              (
                item,
                index
              ) => {
                const active =
                  index === step;
                const completed =
                  index < step;
                return (
                  <React.Fragment
                    key={
                      item.id
                    }
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const reviewLocked =
                          !isEdit &&
                          currentStep.id === "review" &&
                          Boolean(savedStudentMaster);
                        if (reviewLocked) {
                          return;
                        }
                        if (
                          index <= step
                        ) {
                          goToStep(
                            index
                          );
                        }
                      }}
                      style={{
                        border:
                          "none",
                        background:
                          "transparent",
                        padding: 0,
                        cursor:
                          index <=
                            step
                            ? "pointer"
                            : "default",
                        minWidth:
                          75,
                        display:
                          "flex",
                        flexDirection:
                          "column",
                        alignItems:
                          "center",
                        gap: 5,
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius:
                            "50%",
                          display:
                            "flex",
                          alignItems:
                            "center",
                          justifyContent:
                            "center",
                          background:
                            active ||
                              completed
                              ? (publicRegistration ? "#4f46e5" : "var(--p)")
                              : (publicRegistration ? "#dbeafe" : "#eef2f7"),
                          color:
                            active ||
                              completed
                              ? "#fff"
                              : "var(--t3)",
                          fontSize:
                            10,
                          fontWeight:
                            700,
                        }}
                      >
                        {completed
                          ? "✓"
                          : index +
                          1}
                      </div>
                      <span
                        style={{
                          fontSize:
                            9.5,
                          fontWeight:
                            active
                              ? 700
                              : 500,
                          color:
                            active
                              ? (publicRegistration ? "#4338ca" : "var(--p)")
                              : "var(--t3)",
                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {
                          item.short
                        }
                      </span>
                    </button>
                    {index <
                      visibleSteps.length -
                      1 && (
                        <div
                          style={{
                            flex: 1,
                            minWidth: 15,
                            height: 1,
                            marginTop: 14,
                            background:
                              index <
                                step
                                ? "var(--p)"
                                : "#e5e7eb",
                          }}
                        />
                      )}
                  </React.Fragment>
                );
              }
            )}
          </div>
          {/* CURRENT STEP */}
          <div
            style={{
              marginBottom: 20,
            }}
          >
            {
              renderCurrentStep()
            }
          </div>
          {errors.update && (
            <div
              style={{
                marginBottom: 12,
                padding: 10,
                borderRadius: 7,
                background:
                  "#fef2f2",
                color:
                  "#dc2626",
                fontSize: 11,
              }}
            >
              {errors.update}
            </div>
          )}
          {/* NAVIGATION */}
          <div
            style={{
              display:
                "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: 9,
              borderTop:
                "1px solid #eef2f7",
              paddingTop: 15,
            }}
          >
            <button
              className="btn btn-g"
              type="button"
              onClick={handleBackStep}
              disabled={
                !isEdit &&
                currentStep.id === "review" &&
                Boolean(savedStudentMaster)
              }
              style={{
                opacity:
                  !isEdit &&
                    currentStep.id === "review" &&
                    savedStudentMaster
                    ? 0.5
                    : 1,
                cursor:
                  !isEdit &&
                    currentStep.id === "review" &&
                    savedStudentMaster
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {step === 0
                ? "Cancel"
                : "Back"}
            </button>
            <div
              style={{
                display:
                  "flex",
                gap: 9,
              }}
            >
              {/* =========================================
                NORMAL NEXT BUTTON
                - Add: until Review
                - Edit: until Review
               ========================================= */}
              {step <
                visibleSteps.length -
                1 && (
                  <button
                    className="btn btn-p"
                    type="button"
                    disabled={
                      paymentProcessing
                    }
                    onClick={
                      handleNext
                    }
                  >
                    {paymentProcessing &&
                      (currentStep.id ===
                        "courier" ||
                        currentStep.id ===
                        "review")
                      ? "Saving..."
                      : "Next"}
                    <span
                      style={{
                        marginLeft: 4,
                      }}
                    >
                      →
                    </span>
                  </button>
                )}
              {/* =========================================
                EDIT MODE FINAL BUTTON
                Review is the last step.
               ========================================= */}
              {isEdit &&
                currentStep.id ===
                "review" && (
                  <button
                    className="btn btn-p"
                    type="button"
                    disabled={
                      paymentProcessing
                    }
                    onClick={
                      handleNext
                    }
                  >
                    <I
                      n="check"
                      s={13}
                      c="#fff"
                    />
                    {paymentProcessing
                      ? "Updating..."
                      : "Update Student"}
                  </button>
                )}
              {/* =========================================
                ADD MODE PAYMENT BUTTON
                Existing add/payment flow unchanged.
               ========================================= */}
              {!isEdit &&
                step ===
                visibleSteps.length -
                1 && (
                  <button
                    className="btn btn-p"
                    type="button"
                    disabled={
                      paymentProcessing ||
                      !savedStudentMaster
                    }
                    onClick={
                      handlePayment
                    }
                  >
                    <I
                      n="check"
                      s={13}
                      c="#fff"
                    />
                    {paymentProcessing
                      ? "Processing..."
                      : typeof onStartPayment !==
                        "function"
                        ? "Payment API Pending"
                        : feeSummary.finalAmount >
                          0
                          ? `Pay ${formatINR(
                            feeSummary.finalAmount
                          )}`
                          : "Continue"}
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>
      {registrationConfirmOpen && !isEdit && (
        <div
          role="presentation"
          className="modal-bg"
          style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(15,23,42,.55)", display: "flex", justifyContent: "center", alignItems: "center", padding: 16 }}
          onClick={() => { if (!paymentProcessing) setRegistrationConfirmOpen(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="student-registration-confirm-title"
            className="modal modal-sm"
            style={{ background: "#fff", width: "100%", maxWidth: 440, borderRadius: 14, padding: 24, boxShadow: "0 15px 55px rgba(0,0,0,.22)" }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="student-registration-confirm-title" style={{ fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>
              Confirm Registration / नोंदणीची खात्री करा
            </h3>
            <p style={{ margin: "0 0 10px", fontSize: 14 }}>
              Are you sure you want to register this student?
            </p>
            <p style={{ margin: "0 0 20px", fontSize: 14 }}>
              तुम्हाला खात्री आहे का की या विद्यार्थ्याची नोंदणी करायची आहे?
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="btn btn-g" disabled={paymentProcessing}
                onClick={() => setRegistrationConfirmOpen(false)}>
                Cancel / रद्द करा
              </button>
              <button type="button" className="btn btn-p" disabled={paymentProcessing}
                onClick={async () => {
                  if (paymentProcessing) return;
                  setRegistrationConfirmOpen(false);
                  await handleNext(true);
                }}>
                {paymentProcessing ? "Saving... / जतन होत आहे..." : "Confirm / खात्री करा"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
/* =========================================================
   REVIEW SECTION
========================================================= */
function ReviewSection({
  title,
  onEdit,
  children,
}) {
  return (
    <section
      style={{
        border:
          "1px solid #e5e7eb",
        borderRadius: 8,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display:
            "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          padding:
            "10px 12px",
          background:
            "#f8fafc",
          borderBottom:
            "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            fontSize:
              11.5,
            fontWeight:
              700,
          }}
        >
          {title}
        </div>
        {typeof onEdit === "function" && (
          <button
            type="button"
            onClick={onEdit}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--p)",
              fontSize: 10,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        )}
      </div>
      <div
        style={{
          padding: 12,
          display:
            "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        {children}
      </div>
    </section>
  );
}
/* =========================================================
   REVIEW ROW
========================================================= */
function ReviewRow({
  label,
  value,
}) {
  return (
    <div>
      <div
        style={{
          fontSize:
            9.5,
          color:
            "var(--t3)",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize:
            11,
          fontWeight:
            600,
          color:
            "var(--t2)",
          wordBreak:
            "break-word",
        }}
      >
        {value || "—"}
      </div>
    </div>
  );
}
/* =========================================================
   PAYMENT ROW
========================================================= */
function PaymentRow({
  label,
  amount,
}) {
  return (
    <div
      style={{
        display:
          "flex",
        justifyContent:
          "space-between",
        alignItems:
          "center",
        fontSize: 11,
      }}
    >
      <span
        style={{
          color:
            "var(--t2)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontWeight:
            600,
        }}
      >
        {formatINR(
          amount
        )}
      </span>
    </div>
  );
}
/* =========================================================
   FEE SUMMARY
========================================================= */
function FeeSummary({
  summary,
}) {
  return (
    <div
      style={{
        border:
          "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 14,
        background:
          "#fff",
        marginTop: 12,
      }}
    >
      <div
        style={{
          fontWeight:
            700,
          fontSize: 12,
          marginBottom:
            12,
        }}
      >
        Fee Summary
      </div>
      <PaymentRow
        label="Examination Fees / परीक्षा शुल्क"
        amount={
          summary.examFeeTotal
        }
      />
      <PaymentRow
        label="Study Material / अभ्यास साहित्य"
        amount={
          summary.materialTotal
        }
      />
      <PaymentRow
        label="पुस्तकांचे कुरिअर (₹50 प्रति परीक्षा)"
        amount={
          summary.courierCharge
        }
      />
      <div
        style={{
          borderTop:
            "1px solid #eef2f7",
          marginTop: 10,
          paddingTop: 10,
          display:
            "flex",
          justifyContent:
            "space-between",
          fontWeight:
            700,
          fontSize: 13,
        }}
      >
        <span>
          Total
        </span>
        <span>
          {formatINR(
            summary.finalAmount
          )}
        </span>
      </div>
    </div>
  );
}
